import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Tab, Tabs, Spinner, Modal, Alert, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import AdminSectionNav from './AdminSectionNav';
import SupportOrganizations from './SupportOrganizations';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import PartnerPriceListTermsEditor from './PartnerPriceListTermsEditor';
import SubscriptionPlansOverview from './SubscriptionPlansOverview';
import PartnerAssignmentTermsModal from './PartnerAssignmentTermsModal';
import Affiliates from './Affiliates';
import PartnerApplications from './PartnerApplications';
import OrganizationsTable from './OrganizationsTable';
import MediaStorageAdmin from './MediaStorageAdmin';
import axios from 'axios';
import UOMManagement from './UOMManagement';
import MaterialMaster from './MaterialMaster';
import ServicesMaster from './ServicesMaster';
import AnnouncementsAdmin from './AnnouncementsAdmin';
import AssistantQuestionsAdmin from './AssistantQuestionsAdmin';
import EmailBroadcastAdmin from './EmailBroadcastAdmin';
import { countries, getStatesForCountry } from '../utils/countriesStates';
import { isOrgAdminRole, formatRoleName, getRoleLabel, ORG_ADMIN_ROLE } from '../utils/roleUtils';
import OrgPrintChrome from './OrgPrintChrome';
import PhoneNumberInput, { isValidPhoneNumber } from './PhoneNumberInput';
import useSectionTab from '../hooks/useSectionTab';
import WebAddressInput, { checkWebAddress } from './WebAddressInput';
import { localISODate, todayISO } from '../utils/dates';
import PasswordHint from './PasswordHint';
import SalesFunnel from './SalesFunnel';
import SupportTickets from './SupportTickets';
import ShopDirectoryAdmin from './ShopDirectoryAdmin';
import FindByRef from './FindByRef';
import { useSearchParams } from 'react-router-dom';
import { passwordProblem } from '../utils/passwordPolicy';


import DateInput from './DateInput';
import { formatDate, formatDateTime } from '../utils/dateFormat';
const SYSTEM_ADMIN_SECTIONS = [
  { items: [{ key: 'dashboard', label: 'Dashboard', icon: 'bi-speedometer2' }] },
  {
    group: 'Organizations and access',
    items: [
      { key: 'organizations', label: 'Organizations', icon: 'bi-building' },
      { key: 'system-admins', label: 'System Admins', icon: 'bi-person-badge' }
    ]
  },
  {
    group: 'Masters',
    items: [
      { key: 'uom', label: 'Units of Measurement', icon: 'bi-rulers' },
      { key: 'goods-movement-types', label: 'Goods Movement Types', icon: 'bi-arrow-left-right' },
      { key: 'tax-rates', label: 'Tax Rates', icon: 'bi-percent' },
      { key: 'ledger-categories', label: 'Ledger Categories', icon: 'bi-tags' }
    ]
  },
  {
    group: 'Plans and partners',
    items: [
      { key: 'subscription-plans', label: 'Subscription Plans', icon: 'bi-card-list' },
      { key: 'subscription-plan-requests', label: 'Plan Requests', icon: 'bi-inbox' },
      { key: 'partners', label: 'Account Managers', icon: 'bi-people' },
      { key: 'partner-applications', label: 'Applications', icon: 'bi-person-plus' },
      { key: 'affiliates', label: 'Affiliates', icon: 'bi-share' },
      { key: 'partner-commissions', label: 'Partner Commissions', icon: 'bi-cash-coin' }
    ]
  },
  {
    group: 'Communication',
    items: [
      { key: 'email', label: 'Email', icon: 'bi-envelope' },
      { key: 'email-broadcast', label: 'Broadcast Email', icon: 'bi-send' },
      { key: 'announcements', label: 'Announcements', icon: 'bi-megaphone' }
    ]
  },
  {
    group: 'Platform',
    items: [
      { key: 'media', label: 'Media', icon: 'bi-images' }
    ]
  }
];

// The Tech Support module (iBigDo staff who answer customers; System Admin sees it too). Same page, its own menu entry and
// addresses (/tech-support/<section>): customers and their requests, and the Global Material Master's review.
const TECH_SUPPORT_SECTIONS = [
  { items: [{ key: 'dashboard', label: 'Dashboard', icon: 'bi-speedometer2' }] },
  {
    group: 'Customers',
    items: [
      { key: 'organizations', label: 'Organizations', icon: 'bi-building' },
      { key: 'feedback', label: 'Feedback', icon: 'bi-chat-square-text' },
      { key: 'assistant-questions', label: 'Assistant Questions', icon: 'bi-chat-dots' },
      { key: 'shop-directory', label: 'Shop Directory', icon: 'bi-grid-3x3-gap' },
      { key: 'find-by-ref', label: 'Find by Ref', icon: 'bi-search' }
    ]
  },
  {
    group: 'Global masters',
    items: [
      { key: 'material-master', label: 'Material Master', icon: 'bi-box-seam' },
      { key: 'services-master', label: 'Services Master', icon: 'bi-tools' }
    ]
  }
];

const keysOf = (sections) => sections.flatMap((s) => s.items.map((i) => i.key));
const SYSTEM_ADMIN_SECTION_KEYS = keysOf(SYSTEM_ADMIN_SECTIONS);
const TECH_SUPPORT_SECTION_KEYS = keysOf(TECH_SUPPORT_SECTIONS);


// How a role was given or taken (role_changes.reason), in words
const ROLE_CHANGE_REASONS = {
  ASSIGNED: 'Given by an admin',
  REMOVED: 'Taken by an admin',
  REGISTERED: 'Registered the organization',
  SELF_ASSIGNED: 'Took Org Admin (only member)',
  ACCOUNT_MANAGER_ASSIGNED: 'Account Manager assigned',
  ACCOUNT_MANAGER_ENDED: 'Account Manager assignment ended',
  LEFT_ORGANIZATION: 'Removed from the organization'
};

/** `module` is 'system-admin' (platform settings, System Admin only) or 'tech-support' (customer support and catalog review). */
/** A small icon button with its name shown on hover (and read out), for a row of actions. */
function IconAction({ icon, label, variant, onClick }) {
  return (
    <OverlayTrigger placement="top" overlay={<Tooltip id={`action-${label.replace(/\s+/g, '-')}`}>{label}</Tooltip>}>
      <Button size="sm" variant={variant} onClick={onClick} aria-label={label}>
        <i className={`bi bi-${icon}`} aria-hidden="true" />
      </Button>
    </OverlayTrigger>
  );
}

function SystemAdmin({ module = 'system-admin' }) {
  const { user } = useAuth();
  const supportModule = module === 'tech-support';
  const base = supportModule ? '/tech-support' : '/system-admin';
  // Tech Support, not System Admin: a look at organizations rather than their controls
  const isSupportOnly = !user?.isSystemAdmin && !!user?.isTechSupport;
  const visibleSections = supportModule ? TECH_SUPPORT_SECTIONS : SYSTEM_ADMIN_SECTIONS;
  const [loading, setLoading] = useState(true);

  // The open section is in the address (/system-admin/<section> or /tech-support/<section>)
  const [activeTab, setActiveTab] = useSectionTab(base, supportModule ? TECH_SUPPORT_SECTION_KEYS : SYSTEM_ADMIN_SECTION_KEYS);
  const [searchParams, setSearchParams] = useSearchParams();

  const [versionInfo, setVersionInfo] = useState(null);
  useEffect(() => {
    if (isSupportOnly || supportModule) return;
    axios.get('/api/system-admin/version')
      .then((r) => setVersionInfo(r.data))
      .catch(() => {});
  }, [isSupportOnly, supportModule]);

  const [dashboard, setDashboard] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgRoles, setOrgRoles] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [systemAdmins, setSystemAdmins] = useState([]);
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [showDemoteModal, setShowDemoteModal] = useState(false);
  const [showAssignRoleModal, setShowAssignRoleModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [adminRoleId, setAdminRoleId] = useState(null);

  // Org user/role management (under Organizations → View Details)
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userFormData, setUserFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    password: '',
    isActive: true
  });
  const [showRoleAssignmentModal, setShowRoleAssignmentModal] = useState(false);
  const [selectedUserForRoleAssignment, setSelectedUserForRoleAssignment] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleFormData, setRoleFormData] = useState({
    name: '',
    description: '',
    isActive: true
  });
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [permissionsGrouped, setPermissionsGrouped] = useState({});
  const [rolePermissions, setRolePermissions] = useState([]);
  
  // Ledger Categories state
  const [ledgerCategories, setLedgerCategories] = useState([]);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryFormData, setCategoryFormData] = useState({
    categoryName: '',
    type: '',
    description: '',
    isActive: true
  });
  const [categoryFilter, setCategoryFilter] = useState('ALL');

  // Goods Movement Types state
  const [goodsMovementTypes, setGoodsMovementTypes] = useState([]);
  const [showMovementTypeModal, setShowMovementTypeModal] = useState(false);
  const [selectedMovementType, setSelectedMovementType] = useState(null);
  const [movementTypeFormData, setMovementTypeFormData] = useState({
    code: '',
    name: '',
    direction: '',
    referenceType: '',
    locationType: '',
    description: '',
    isActive: true
  });
  const [movementTypeFilter, setMovementTypeFilter] = useState('ALL');

  // Tax Rates state
  const [taxRates, setTaxRates] = useState([]);
  const [showTaxRateModal, setShowTaxRateModal] = useState(false);
  const [selectedTaxRate, setSelectedTaxRate] = useState(null);
  const [taxRateFormData, setTaxRateFormData] = useState({
    name: '',
    ratePercent: '',
    description: '',
    isActive: true
  });

  // Subscription Plans state
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [planFeatureCatalog, setPlanFeatureCatalog] = useState([]);
  const [subscriptionPlanRequests, setSubscriptionPlanRequests] = useState([]);
  const [showRejectRequestModal, setShowRejectRequestModal] = useState(false);
  const [rejectingRequestId, setRejectingRequestId] = useState(null);
  const [rejectNotes, setRejectNotes] = useState('');
  const [showSubscriptionPlanModal, setShowSubscriptionPlanModal] = useState(false);
  const [selectedSubscriptionPlan, setSelectedSubscriptionPlan] = useState(null);
  const [subscriptionPlanFormData, setSubscriptionPlanFormData] = useState({
    name: '',
    description: '',
    priceMonthly: '',
    priceYearly: '',
    trialDays: '',
    maxWarehouses: '',
    maxProductionUnits: '',
    maxRetailStores: '',
    maxDailyTransactions: '',
    maxUsers: '',
    maxDocumentsPerMonth: '',
    features: [],
    audience: '',
    onboardingFee: '',
    onboardingRequired: false,
    supportResponseDays: '',
    accountManagerResponseDays: '',
    visibleToPartners: false,
    isDefaultPlan: false,
    isActive: true
  });
  const [orgSubscriptionUsage, setOrgSubscriptionUsage] = useState(null);
  const [orgUsageMetrics, setOrgUsageMetrics] = useState(null);
  const [orgSubscriptionHistory, setOrgSubscriptionHistory] = useState([]);
  const [orgRoleChanges, setOrgRoleChanges] = useState([]);
  const [selectedOrgFull, setSelectedOrgFull] = useState(null);
  const [subscriptionAssignForm, setSubscriptionAssignForm] = useState({ subscriptionPlanId: '', subscriptionStatus: '', subscriptionStartDate: '', subscriptionEndDate: '' });
  const [subscriptionAssignSaving, setSubscriptionAssignSaving] = useState(false);

  // Account Managers (formerly "Partners") state
  const [partners, setPartners] = useState([]);
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [partnerFormData, setPartnerFormData] = useState({
    name: '', contactPerson: '', email: '', phone: '', notes: '', isActive: true
  });
  const [orgPartnerAssignments, setOrgPartnerAssignments] = useState([]);
  const emptyPartnerAssignForm = {
    partnerId: '', initialRatePercent: '', trailingRatePercent: '', initialPeriodMonths: '',
    capAmount: '', capMonths: '', startDate: todayISO(), createLogin: false
  };
  const [partnerAssignForm, setPartnerAssignForm] = useState(emptyPartnerAssignForm);
  const [partnerAssignSaving, setPartnerAssignSaving] = useState(false);
  const [partnerCommissions, setPartnerCommissions] = useState([]);
  const [commissionPartnerFilter, setCommissionPartnerFilter] = useState('ALL');
  const [commissionStatusFilter, setCommissionStatusFilter] = useState('ALL');
  // Recording a refund of a payment: the commission line it was opened from, what the payment shows, and the form
  const [refundFor, setRefundFor] = useState(null);
  const [refundInfo, setRefundInfo] = useState(null);
  const [refundForm, setRefundForm] = useState({ amount: '', reason: '' });
  const [refundBusy, setRefundBusy] = useState(false);

  // Email settings
  const [emailSettings, setEmailSettings] = useState({
    enabled: false,
    host: '',
    port: 587,
    username: '',
    password: '',
    passwordConfigured: false,
    fromAddress: '',
    fromName: '',
    useTls: true,
    frontendBaseUrl: '',
    feedbackNotifyEmail: '',
    effectiveConfigured: false,
    activeSource: 'none',
    environmentFallbackAvailable: false
  });
  const [emailSettingsLoading, setEmailSettingsLoading] = useState(false);
  const [emailSettingsSaving, setEmailSettingsSaving] = useState(false);
  const [testEmailTo, setTestEmailTo] = useState('');
  const [testEmailSending, setTestEmailSending] = useState(false);

  // Feedback inbox
  const [feedbackItems, setFeedbackItems] = useState([]);
  const [feedbackFilter, setFeedbackFilter] = useState('ALL');
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState({
    status: 'NEW',
    adminNotes: '',
    response: '',
    notifyUser: true
  });
  const [feedbackSaving, setFeedbackSaving] = useState(false);

  // Print view state
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [printType, setPrintType] = useState(null);

  // Organization creation state
  const [showCreateOrgPanel, setShowCreateOrgPanel] = useState(false);
  const [orgFormData, setOrgFormData] = useState({
    name: '',
    email: '',
    description: '',
    phoneNumber: '',
    address: '',
    city: '',
    district: '',
    state: '',
    country: '',
    postalCode: '',
    website: '',
    defaultCurrency: 'USD',
    logoUrl: '',
    isActive: true
  });
  const [selectedOrgCountryCode, setSelectedOrgCountryCode] = useState('');

  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchDashboard();
    } else if (activeTab === 'organizations') {
      fetchOrganizations();
    } else if (activeTab === 'system-admins') {
      fetchSystemAdmins();
      fetchTechSupport();
    } else if (activeTab === 'ledger-categories') {
      fetchLedgerCategories();
    } else if (activeTab === 'goods-movement-types') {
      fetchGoodsMovementTypes();
    } else if (activeTab === 'tax-rates') {
      fetchTaxRates();
    } else if (activeTab === 'subscription-plans') {
      fetchSubscriptionPlans();
    } else if (activeTab === 'subscription-plan-requests') {
      fetchSubscriptionPlanRequests();
    } else if (activeTab === 'partners') {
      fetchPartners();
    } else if (activeTab === 'partner-commissions') {
      fetchPartnerCommissions();
    } else if (activeTab === 'email') {
      fetchEmailSettings();
    } else if (activeTab === 'feedback') {
      fetchFeedback();
    } else {
      // For other tabs (uom, material-master, services-master), set loading to false
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    // Tech Support's own organization view loads what it needs
    if (selectedOrg && !isSupportOnly) {
      fetchOrgDetails(selectedOrg.id);
    }
  }, [selectedOrg]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get('/api/system-admin/dashboard');
      setDashboard(response.data);
    } catch (error) {
      toast.error('Error fetching dashboard data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/system-admin/organizations');
      if (Array.isArray(response.data) && response.data.length > 0) {
        setOrganizations(response.data);
      } else if (dashboard && Array.isArray(dashboard.organizations) && dashboard.organizations.length > 0) {
        // Fallback: use dashboard organizations summary if full list is empty/unavailable
        const mapped = dashboard.organizations.map((o) => ({
          id: o.id,
          name: o.name,
          code: o.code,
          email: ''
        }));
        setOrganizations(mapped);
      } else {
        setOrganizations([]);
      }
    } catch (error) {
      toast.error('Error fetching organizations');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgDetails = async (orgId) => {
    try {
      const [rolesResponse, usersResponse, detailsResponse, partnerAssignmentsResponse] = await Promise.all([
        axios.get(`/api/system-admin/organizations/${orgId}/roles`),
        axios.get(`/api/system-admin/organizations/${orgId}/users`),
        axios.get(`/api/system-admin/organizations/${orgId}/details`),
        axios.get(`/api/system-admin/organizations/${orgId}/partner-assignments`)
      ]);
      setOrgRoles(rolesResponse.data);
      setOrgUsers(usersResponse.data);
      setOrgSubscriptionUsage(detailsResponse.data.subscriptionUsage || null);
      setOrgUsageMetrics(detailsResponse.data.usageMetrics || null);
      setOrgSubscriptionHistory(detailsResponse.data.subscriptionHistory || []);
      // Role history is informational: a failure here shouldn't stop the rest of the panel
      axios.get(`/api/system-admin/organizations/${orgId}/role-changes`)
        .then((r) => setOrgRoleChanges(Array.isArray(r.data) ? r.data : []))
        .catch(() => setOrgRoleChanges([]));
      setSelectedOrgFull(detailsResponse.data.organization);
      setSubscriptionAssignForm({
        subscriptionPlanId: detailsResponse.data.organization?.subscriptionPlan?.id || '',
        subscriptionStatus: detailsResponse.data.organization?.subscriptionStatus || '',
        subscriptionStartDate: detailsResponse.data.organization?.subscriptionStartDate ? detailsResponse.data.organization.subscriptionStartDate.slice(0, 10) : '',
        subscriptionEndDate: detailsResponse.data.organization?.subscriptionEndDate ? detailsResponse.data.organization.subscriptionEndDate.slice(0, 10) : ''
      });
      if (subscriptionPlans.length === 0) {
        axios.get('/api/system-admin/subscription-plans').then((r) => setSubscriptionPlans(r.data)).catch(() => {});
      }
      if (partners.length === 0) {
        axios.get('/api/system-admin/partners').then((r) => setPartners(r.data)).catch(() => {});
      }
      setOrgPartnerAssignments(partnerAssignmentsResponse.data || []);
      setPartnerAssignForm(emptyPartnerAssignForm);

      // Find ADMIN role ID
      const adminRole = rolesResponse.data.find(r => r.name === ORG_ADMIN_ROLE || r.name === 'ADMIN');
      if (adminRole) {
        setAdminRoleId(adminRole.id);
      } else {
        setAdminRoleId(null);
      }
    } catch (error) {
      toast.error('Error fetching organization details');
      console.error(error);
    }
  };

  const fetchSystemAdmins = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/system-admin/system-admins');
      setSystemAdmins(response.data);
    } catch (error) {
      toast.error('Error fetching system admins');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Tech Support: iBigDo staff who answer customers; chosen from the people of any organization (the staff are in iBigDo's own)
  const [techSupport, setTechSupport] = useState([]);
  const [techOrgId, setTechOrgId] = useState('');
  const [techOrgUsers, setTechOrgUsers] = useState([]);
  const [techUserId, setTechUserId] = useState('');

  const fetchTechSupport = async () => {
    try {
      const [people, orgs] = await Promise.all([
        axios.get('/api/system-admin/tech-support'),
        organizations.length === 0 ? axios.get('/api/system-admin/organizations') : Promise.resolve({ data: organizations })
      ]);
      setTechSupport(people.data);
      if (organizations.length === 0 && Array.isArray(orgs.data)) setOrganizations(orgs.data);
    } catch (error) {
      toast.error('Error fetching Tech Support');
    }
  };

  const chooseTechOrg = async (orgId) => {
    setTechOrgId(orgId);
    setTechUserId('');
    setTechOrgUsers([]);
    if (!orgId) return;
    try {
      const r = await axios.get(`/api/system-admin/organizations/${orgId}/users`);
      setTechOrgUsers((r.data || []).filter((u) => u.isActive !== false && !u.isSystemAdmin && !u.isTechSupport));
    } catch (error) {
      toast.error('Could not load that organization\'s people');
    }
  };

  const giveTechSupport = async () => {
    if (!techUserId) return;
    try {
      await axios.put(`/api/system-admin/users/${techUserId}/promote-tech-support`);
      toast.success('Tech Support access given');
      setTechUserId('');
      await chooseTechOrg(techOrgId);
      fetchTechSupport();
    } catch (error) {
      const msg = error.response?.data;
      toast.error(typeof msg === 'string' && msg ? msg : 'Could not give Tech Support access');
    }
  };

  const takeTechSupport = async (person) => {
    if (!window.confirm(`Take Tech Support access away from ${person.email}?`)) return;
    try {
      await axios.put(`/api/system-admin/users/${person.id}/demote-tech-support`);
      toast.success('Tech Support access taken away');
      fetchTechSupport();
    } catch (error) {
      toast.error('Could not take the access away');
    }
  };

  const fetchLedgerCategories = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/system-admin/ledger-categories');
      setLedgerCategories(response.data);
    } catch (error) {
      toast.error('Error fetching ledger categories');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGoodsMovementTypes = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/system-admin/goods-movement-types');
      setGoodsMovementTypes(response.data);
    } catch (error) {
      toast.error('Error fetching goods movement types');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTaxRates = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/system-admin/tax-rates');
      setTaxRates(response.data);
    } catch (error) {
      toast.error('Error fetching tax rates');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionPlans = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/system-admin/subscription-plans');
      setSubscriptionPlans(response.data);
      axios.get('/api/system-admin/subscription-plan-features').then((r) => setPlanFeatureCatalog(r.data || [])).catch(() => {});
    } catch (error) {
      toast.error('Error fetching subscription plans');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionPlanRequests = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/system-admin/subscription-plan-requests');
      setSubscriptionPlanRequests(response.data);
    } catch (error) {
      toast.error('Error fetching subscription plan requests');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPartners = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/system-admin/partners');
      setPartners(response.data);
    } catch (error) {
      toast.error('Error fetching Account Managers');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPartnerCommissions = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/system-admin/partner-commissions');
      setPartnerCommissions(response.data);
    } catch (error) {
      toast.error('Error fetching Account Manager commissions');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveRequest = async (id) => {
    if (!window.confirm('Approve this subscription request? The plan will be activated for the organization immediately.')) {
      return;
    }
    try {
      await axios.post(`/api/system-admin/subscription-plan-requests/${id}/approve`);
      toast.success('Request approved');
      fetchSubscriptionPlanRequests();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error approving request';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error approving request');
    }
  };

  const handleOpenRejectRequest = (id) => {
    setRejectingRequestId(id);
    setRejectNotes('');
    setShowRejectRequestModal(true);
  };

  const handleRejectRequest = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`/api/system-admin/subscription-plan-requests/${rejectingRequestId}/reject`, {
        notes: rejectNotes.trim() || null
      });
      toast.success('Request rejected');
      setShowRejectRequestModal(false);
      fetchSubscriptionPlanRequests();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error rejecting request';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error rejecting request');
    }
  };

  const fetchEmailSettings = async () => {
    try {
      setLoading(true);
      setEmailSettingsLoading(true);
      const response = await axios.get('/api/system-admin/email-settings');
      const data = response.data || {};
      setEmailSettings({
        enabled: !!data.enabled,
        host: data.host || '',
        port: data.port || 587,
        username: data.username || '',
        password: '',
        passwordConfigured: !!data.passwordConfigured,
        fromAddress: data.fromAddress || '',
        fromName: data.fromName || '',
        useTls: data.useTls !== false,
        frontendBaseUrl: data.frontendBaseUrl || '',
        feedbackNotifyEmail: data.feedbackNotifyEmail || '',
        effectiveConfigured: !!data.effectiveConfigured,
        activeSource: data.activeSource || 'none',
        environmentFallbackAvailable: !!data.environmentFallbackAvailable
      });
    } catch (error) {
      toast.error('Error fetching email settings');
      console.error(error);
    } finally {
      setEmailSettingsLoading(false);
      setLoading(false);
    }
  };

  const handleSaveEmailSettings = async (e) => {
    e.preventDefault();
    const frontendBaseUrl = checkWebAddress('Frontend base URL', emailSettings.frontendBaseUrl);
    if (frontendBaseUrl.error) {
      toast.error(frontendBaseUrl.error);
      return;
    }
    try {
      setEmailSettingsSaving(true);
      const payload = {
        enabled: emailSettings.enabled,
        host: emailSettings.host,
        port: Number(emailSettings.port) || 587,
        username: emailSettings.username,
        fromAddress: emailSettings.fromAddress,
        fromName: emailSettings.fromName,
        useTls: emailSettings.useTls,
        frontendBaseUrl: frontendBaseUrl.value,
        feedbackNotifyEmail: emailSettings.feedbackNotifyEmail
      };
      if (emailSettings.password) {
        payload.password = emailSettings.password;
      }
      const response = await axios.put('/api/system-admin/email-settings', payload);
      const data = response.data || {};
      setEmailSettings((prev) => ({
        ...prev,
        enabled: !!data.enabled,
        host: data.host || '',
        port: data.port || 587,
        username: data.username || '',
        password: '',
        passwordConfigured: !!data.passwordConfigured,
        fromAddress: data.fromAddress || '',
        fromName: data.fromName || '',
        useTls: data.useTls !== false,
        frontendBaseUrl: data.frontendBaseUrl || '',
        feedbackNotifyEmail: data.feedbackNotifyEmail || '',
        effectiveConfigured: !!data.effectiveConfigured,
        activeSource: data.activeSource || 'none',
        environmentFallbackAvailable: !!data.environmentFallbackAvailable
      }));
      toast.success('Email settings saved');
    } catch (error) {
      const errorMsg = error.response?.data || 'Error saving email settings';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error saving email settings');
    } finally {
      setEmailSettingsSaving(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmailTo.trim()) {
      toast.error('Enter a recipient email for the test');
      return;
    }
    try {
      setTestEmailSending(true);
      const response = await axios.post('/api/system-admin/email-settings/test', {
        to: testEmailTo.trim()
      });
      toast.success(response.data?.message || 'Test email sent');
    } catch (error) {
      const errorMsg = error.response?.data || 'Failed to send test email';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Failed to send test email');
    } finally {
      setTestEmailSending(false);
    }
  };

  const fetchFeedback = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/system-admin/feedback');
      setFeedbackItems(response.data || []);
    } catch (error) {
      toast.error('Error fetching feedback');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenFeedback = async (item) => {
    try {
      const response = await axios.get(`/api/system-admin/feedback/${item.id}`);
      const detail = response.data || item;
      setSelectedFeedback(detail);
      setFeedbackForm({
        status: detail.status || 'NEW',
        adminNotes: '',
        response: '',
        notifyUser: true
      });
      setShowFeedbackModal(true);
    } catch (error) {
      toast.error('Failed to load feedback thread');
      console.error(error);
    }
  };

  // A ticket linked from the Tech Support dashboard (/tech-support/feedback?ticket=12) opens its thread
  useEffect(() => {
    const id = searchParams.get('ticket');
    if (id && activeTab === 'feedback') {
      handleOpenFeedback({ id });
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, searchParams]);

  const handleSaveFeedback = async (e) => {
    e.preventDefault();
    if (!selectedFeedback?.id) {
      return;
    }
    try {
      setFeedbackSaving(true);
      const payload = {
        status: feedbackForm.status,
        notifyUser: feedbackForm.notifyUser
      };
      // Only send when composing a new entry (append-only)
      if (feedbackForm.response.trim()) {
        payload.response = feedbackForm.response.trim();
      }
      if (feedbackForm.adminNotes.trim()) {
        payload.adminNotes = feedbackForm.adminNotes.trim();
      }
      const response = await axios.put(`/api/system-admin/feedback/${selectedFeedback.id}`, payload);
      toast.success('Feedback updated');
      setSelectedFeedback(response.data);
      setFeedbackForm({
        status: response.data?.status || feedbackForm.status,
        adminNotes: '',
        response: '',
        notifyUser: true
      });
      await fetchFeedback();
    } catch (error) {
      const errorMsg = error.response?.data || 'Failed to update feedback';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Failed to update feedback');
    } finally {
      setFeedbackSaving(false);
    }
  };

  const renderFeedbackMessageAuthor = (msg) => {
    if (msg.authorType === 'ADMIN') {
      return msg.internal ? `Admin (internal) · ${msg.authorName || ''}` : `Admin · ${msg.authorName || ''}`;
    }
    if (msg.authorType === 'SYSTEM') {
      return 'System';
    }
    if (msg.authorType === 'GUEST') {
      return `Guest · ${msg.authorName || ''}`;
    }
    return `User · ${msg.authorName || ''}`;
  };

  const getFeedbackCategoryBadge = (category) => {
    const map = { BUG: 'danger', IDEA: 'primary', COMPLAINT: 'warning', OTHER: 'secondary', CONTACT: 'info' };
    return map[category] || 'secondary';
  };

  const getFeedbackStatusBadge = (status) => {
    const map = {
      NEW: 'warning',
      IN_PROGRESS: 'primary',
      RESOLVED: 'info',
      REOPEN: 'danger',
      CLOSED: 'success',
      REVIEWED: 'secondary'
    };
    return map[status] || 'secondary';
  };

  const formatFeedbackStatus = (status) => {
    const map = {
      NEW: 'New',
      IN_PROGRESS: 'In Progress',
      RESOLVED: 'Resolved',
      REOPEN: 'Reopen',
      CLOSED: 'Closed'
    };
    return map[status] || status;
  };

  const getLedgerTypeBadge = (type) => {
    const badges = {
      'ASSET': 'success',
      'LIABILITY': 'danger',
      'EQUITY': 'info',
      'REVENUE': 'primary',
      'EXPENSE': 'warning'
    };
    return badges[type] || 'secondary';
  };

  const handleCreateCategory = () => {
    setCategoryFormData({
      categoryName: '',
      type: '',
      description: '',
      isActive: true
    });
    setSelectedCategory(null);
    setShowCategoryModal(true);
  };

  const handleEditCategory = (category) => {
    setCategoryFormData({
      categoryName: category.categoryName,
      type: category.type,
      description: category.description || '',
      isActive: category.isActive
    });
    setSelectedCategory(category);
    setShowCategoryModal(true);
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        categoryName: categoryFormData.categoryName.trim(),
        type: categoryFormData.type,
        description: categoryFormData.description.trim() || null,
        isActive: categoryFormData.isActive
      };

      if (selectedCategory) {
        await axios.put(`/api/system-admin/ledger-categories/${selectedCategory.id}`, payload);
        toast.success('Category updated successfully');
      } else {
        await axios.post('/api/system-admin/ledger-categories', payload);
        toast.success('Category created successfully');
      }
      setShowCategoryModal(false);
      fetchLedgerCategories();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error saving category';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error saving category');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) {
      return;
    }
    try {
      await axios.delete(`/api/system-admin/ledger-categories/${id}`);
      toast.success('Category deleted successfully');
      fetchLedgerCategories();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error deleting category';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error deleting category');
    }
  };

  const getMovementDirectionBadge = (direction) => {
    return direction === 'INBOUND' ? 'success' : 'warning';
  };

  const handleCreateMovementType = () => {
    setMovementTypeFormData({
      code: '',
      name: '',
      direction: '',
      referenceType: '',
      locationType: '',
      description: '',
      isActive: true
    });
    setSelectedMovementType(null);
    setShowMovementTypeModal(true);
  };

  const handleViewPrint = (data, type) => {
    setPrintData(data);
    setPrintType(type);
    setShowPrintModal(true);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleEditMovementType = (mt) => {
    setMovementTypeFormData({
      code: mt.code,
      name: mt.name,
      direction: mt.direction,
      referenceType: mt.referenceType || '',
      locationType: mt.locationType || '',
      description: mt.description || '',
      isActive: mt.isActive
    });
    setSelectedMovementType(mt);
    setShowMovementTypeModal(true);
  };

  const handleSaveMovementType = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        code: movementTypeFormData.code.trim(),
        name: movementTypeFormData.name.trim(),
        direction: movementTypeFormData.direction,
        referenceType: movementTypeFormData.referenceType || null,
        locationType: movementTypeFormData.locationType || null,
        description: movementTypeFormData.description.trim() || null,
        isActive: movementTypeFormData.isActive
      };
      if (selectedMovementType) {
        await axios.put(`/api/system-admin/goods-movement-types/${selectedMovementType.id}`, payload);
        toast.success('Goods movement type updated successfully');
      } else {
        await axios.post('/api/system-admin/goods-movement-types', payload);
        toast.success('Goods movement type created successfully');
      }
      setShowMovementTypeModal(false);
      fetchGoodsMovementTypes();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error saving goods movement type';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error saving goods movement type');
    }
  };

  const handleDeleteMovementType = async (id) => {
    if (!window.confirm('Are you sure you want to delete this goods movement type?')) {
      return;
    }
    try {
      await axios.delete(`/api/system-admin/goods-movement-types/${id}`);
      toast.success('Goods movement type deleted successfully');
      fetchGoodsMovementTypes();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error deleting goods movement type';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error deleting goods movement type');
    }
  };

  const handleCreateTaxRate = () => {
    setTaxRateFormData({ name: '', ratePercent: '', description: '', isActive: true });
    setSelectedTaxRate(null);
    setShowTaxRateModal(true);
  };

  const handleEditTaxRate = (rate) => {
    setTaxRateFormData({
      name: rate.name,
      ratePercent: rate.ratePercent != null ? String(rate.ratePercent) : '',
      description: rate.description || '',
      isActive: rate.isActive
    });
    setSelectedTaxRate(rate);
    setShowTaxRateModal(true);
  };

  const handleSaveTaxRate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: taxRateFormData.name.trim(),
        ratePercent: parseFloat(taxRateFormData.ratePercent),
        description: taxRateFormData.description.trim() || null,
        isActive: taxRateFormData.isActive
      };
      if (selectedTaxRate) {
        await axios.put(`/api/system-admin/tax-rates/${selectedTaxRate.id}`, payload);
        toast.success('Tax rate updated successfully');
      } else {
        await axios.post('/api/system-admin/tax-rates', payload);
        toast.success('Tax rate created successfully');
      }
      setShowTaxRateModal(false);
      fetchTaxRates();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error saving tax rate';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error saving tax rate');
    }
  };

  const handleDeleteTaxRate = async (id) => {
    if (!window.confirm('Are you sure you want to delete this tax rate?')) {
      return;
    }
    try {
      await axios.delete(`/api/system-admin/tax-rates/${id}`);
      toast.success('Tax rate deleted successfully');
      fetchTaxRates();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error deleting tax rate';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error deleting tax rate');
    }
  };

  const handleCreateSubscriptionPlan = () => {
    setSubscriptionPlanFormData({
      name: '', description: '', priceMonthly: '', priceYearly: '', trialDays: '',
      maxWarehouses: '', maxProductionUnits: '', maxRetailStores: '', maxDailyTransactions: '', maxUsers: '',
      maxDocumentsPerMonth: '', features: [],
      audience: '', onboardingFee: '', onboardingRequired: false, supportResponseDays: '', accountManagerResponseDays: '', visibleToPartners: false,
      isDefaultPlan: false, isActive: true
    });
    setSelectedSubscriptionPlan(null);
    setShowSubscriptionPlanModal(true);
  };

  const handleEditSubscriptionPlan = (plan) => {
    setSubscriptionPlanFormData({
      name: plan.name,
      description: plan.description || '',
      priceMonthly: plan.priceMonthly != null ? String(plan.priceMonthly) : '',
      priceYearly: plan.priceYearly != null ? String(plan.priceYearly) : '',
      trialDays: plan.trialDays != null ? String(plan.trialDays) : '',
      maxWarehouses: plan.maxWarehouses != null ? String(plan.maxWarehouses) : '',
      maxProductionUnits: plan.maxProductionUnits != null ? String(plan.maxProductionUnits) : '',
      maxRetailStores: plan.maxRetailStores != null ? String(plan.maxRetailStores) : '',
      maxDailyTransactions: plan.maxDailyTransactions != null ? String(plan.maxDailyTransactions) : '',
      maxUsers: plan.maxUsers != null ? String(plan.maxUsers) : '',
      maxDocumentsPerMonth: plan.maxDocumentsPerMonth != null ? String(plan.maxDocumentsPerMonth) : '',
      features: Array.isArray(plan.features) ? plan.features : [],
      audience: plan.audience || '',
      onboardingFee: plan.onboardingFee != null ? String(plan.onboardingFee) : '',
      onboardingRequired: !!plan.onboardingRequired,
      supportResponseDays: plan.supportResponseDays != null ? String(plan.supportResponseDays) : '',
      accountManagerResponseDays: plan.accountManagerResponseDays != null ? String(plan.accountManagerResponseDays) : '',
      visibleToPartners: !!plan.visibleToPartners,
      isDefaultPlan: !!plan.isDefaultPlan,
      isActive: plan.isActive
    });
    setSelectedSubscriptionPlan(plan);
    setShowSubscriptionPlanModal(true);
  };

  const handleSaveSubscriptionPlan = async (e) => {
    e.preventDefault();
    try {
      const toIntOrNull = (v) => (v === '' || v === null || v === undefined ? null : parseInt(v, 10));
      const payload = {
        name: subscriptionPlanFormData.name.trim(),
        description: subscriptionPlanFormData.description.trim() || null,
        priceMonthly: parseFloat(subscriptionPlanFormData.priceMonthly || 0),
        priceYearly: parseFloat(subscriptionPlanFormData.priceYearly || 0),
        trialDays: toIntOrNull(subscriptionPlanFormData.trialDays) || 0,
        maxWarehouses: toIntOrNull(subscriptionPlanFormData.maxWarehouses),
        maxProductionUnits: toIntOrNull(subscriptionPlanFormData.maxProductionUnits),
        maxRetailStores: toIntOrNull(subscriptionPlanFormData.maxRetailStores),
        maxDailyTransactions: toIntOrNull(subscriptionPlanFormData.maxDailyTransactions),
        maxUsers: toIntOrNull(subscriptionPlanFormData.maxUsers),
        maxDocumentsPerMonth: toIntOrNull(subscriptionPlanFormData.maxDocumentsPerMonth),
        features: subscriptionPlanFormData.features,
        audience: subscriptionPlanFormData.audience.trim() || null,
        onboardingFee: subscriptionPlanFormData.onboardingFee === '' ? null : parseFloat(subscriptionPlanFormData.onboardingFee),
        onboardingRequired: subscriptionPlanFormData.onboardingRequired,
        supportResponseDays: toIntOrNull(subscriptionPlanFormData.supportResponseDays),
        accountManagerResponseDays: toIntOrNull(subscriptionPlanFormData.accountManagerResponseDays),
        visibleToPartners: subscriptionPlanFormData.visibleToPartners,
        isDefaultPlan: subscriptionPlanFormData.isDefaultPlan,
        isActive: subscriptionPlanFormData.isActive
      };
      if (selectedSubscriptionPlan) {
        await axios.put(`/api/system-admin/subscription-plans/${selectedSubscriptionPlan.id}`, payload);
        toast.success('Subscription plan updated successfully');
      } else {
        await axios.post('/api/system-admin/subscription-plans', payload);
        toast.success('Subscription plan created successfully');
      }
      setShowSubscriptionPlanModal(false);
      fetchSubscriptionPlans();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error saving subscription plan';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error saving subscription plan');
    }
  };

  const handleDeleteSubscriptionPlan = async (id) => {
    if (!window.confirm('Are you sure you want to delete this subscription plan?')) {
      return;
    }
    try {
      await axios.delete(`/api/system-admin/subscription-plans/${id}`);
      toast.success('Subscription plan deleted successfully');
      fetchSubscriptionPlans();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error deleting subscription plan';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error deleting subscription plan');
    }
  };

  const handleCreatePartner = () => {
    setPartnerFormData({ name: '', contactPerson: '', email: '', phone: '', notes: '', isActive: true });
    setSelectedPartner(null);
    setShowPartnerModal(true);
  };

  const handleEditPartner = (partner) => {
    setPartnerFormData({
      name: partner.name,
      contactPerson: partner.contactPerson || '',
      email: partner.email || '',
      phone: partner.phone || '',
      notes: partner.notes || '',
      isActive: partner.isActive
    });
    setSelectedPartner(partner);
    setShowPartnerModal(true);
  };

  const handleSavePartner = async (e) => {
    e.preventDefault();
    if (partnerFormData.phone && !isValidPhoneNumber(partnerFormData.phone)) {
      toast.error('Phone number doesn\'t look valid');
      return;
    }
    try {
      const payload = {
        name: partnerFormData.name.trim(),
        contactPerson: partnerFormData.contactPerson.trim() || null,
        email: partnerFormData.email.trim() || null,
        phone: partnerFormData.phone.trim() || null,
        notes: partnerFormData.notes.trim() || null,
        isActive: partnerFormData.isActive
      };
      if (selectedPartner) {
        await axios.put(`/api/system-admin/partners/${selectedPartner.id}`, payload);
        toast.success('Account Manager updated successfully');
      } else {
        await axios.post('/api/system-admin/partners', payload);
        toast.success('Account Manager created successfully');
      }
      setShowPartnerModal(false);
      fetchPartners();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error saving Account Manager';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error saving Account Manager');
    }
  };

  const handleDeletePartner = async (id) => {
    if (!window.confirm('Are you sure you want to delete this Account Manager?')) {
      return;
    }
    try {
      await axios.delete(`/api/system-admin/partners/${id}`);
      toast.success('Account Manager deleted successfully');
      fetchPartners();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error deleting Account Manager';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error deleting Account Manager');
    }
  };

  const handleSavePartnerAssignment = async (e) => {
    e.preventDefault();
    if (!selectedOrg) return;
    if (!partnerAssignForm.partnerId) {
      toast.error('Choose an Account Manager');
      return;
    }
    setPartnerAssignSaving(true);
    try {
      await axios.post(`/api/system-admin/organizations/${selectedOrg.id}/partner-assignments`, {
        partnerId: partnerAssignForm.partnerId,
        initialRatePercent: parseFloat(partnerAssignForm.initialRatePercent || 0),
        trailingRatePercent: parseFloat(partnerAssignForm.trailingRatePercent || 0),
        initialPeriodMonths: parseInt(partnerAssignForm.initialPeriodMonths || 0, 10),
        capAmount: partnerAssignForm.capAmount !== '' ? parseFloat(partnerAssignForm.capAmount) : null,
        capMonths: partnerAssignForm.capMonths !== '' ? parseInt(partnerAssignForm.capMonths, 10) : null,
        startDate: partnerAssignForm.startDate ? `${partnerAssignForm.startDate}T00:00:00` : null,
        createLogin: partnerAssignForm.createLogin
      });
      toast.success('Account Manager assigned successfully');
      setPartnerAssignForm(emptyPartnerAssignForm);
      fetchOrgDetails(selectedOrg.id);
    } catch (error) {
      const errorMsg = error.response?.data || 'Error assigning Account Manager';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error assigning Account Manager');
    } finally {
      setPartnerAssignSaving(false);
    }
  };

  const handleEndPartnerAssignment = async (assignmentId) => {
    if (!selectedOrg) return;
    if (!window.confirm('End this Account Manager assignment? No further commission will accrue for this organization from them, and their access to this organization\'s tickets is revoked immediately.')) {
      return;
    }
    try {
      await axios.delete(`/api/system-admin/partner-assignments/${assignmentId}`);
      toast.success('Assignment ended');
      fetchOrgDetails(selectedOrg.id);
    } catch (error) {
      const errorMsg = error.response?.data || 'Error ending assignment';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error ending assignment');
    }
  };

  const openRefund = async (commission) => {
    if (!commission.payment?.id) return;
    setRefundFor(commission);
    setRefundInfo(null);
    setRefundForm({ amount: '', reason: '' });
    try {
      const r = await axios.get(`/api/system-admin/payments/${commission.payment.id}/refunds`);
      setRefundInfo(r.data);
      setRefundForm({ amount: String(r.data.refundable), reason: '' });
    } catch (error) {
      toast.error(typeof error.response?.data === 'string' ? error.response.data : 'Could not load the payment');
      setRefundFor(null);
    }
  };

  const saveRefund = async (e) => {
    e.preventDefault();
    setRefundBusy(true);
    try {
      const r = await axios.post(`/api/system-admin/payments/${refundFor.payment.id}/refunds`, { amount: Number(refundForm.amount), reason: refundForm.reason });
      toast.success(r.data.commissionsReversed > 0 ? `Refund recorded. ${r.data.commissionsReversed} commission line(s) reversed.` : 'Refund recorded. No commission to reverse.');
      setRefundFor(null);
      fetchPartnerCommissions();
    } catch (error) {
      toast.error(typeof error.response?.data === 'string' ? error.response.data : 'Could not record the refund');
    } finally {
      setRefundBusy(false);
    }
  };

  const handleMarkCommissionPaid = async (id) => {
    try {
      await axios.post(`/api/system-admin/partner-commissions/${id}/mark-paid`);
      toast.success('Marked as paid');
      fetchPartnerCommissions();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error marking commission paid';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error marking commission paid');
    }
  };

  const addToDateString = (dateStr, months) => {
    const base = dateStr ? new Date(`${dateStr}T00:00:00`) : new Date();
    base.setMonth(base.getMonth() + months);
    return localISODate(base);
  };

  // A new end date still ahead of today means the plan is running again: an "Expired" status left in the form moves to Trial
  // (the default plan) or Active, so the status and the date agree
  const withEndDate = (form, endDate) => {
    const next = { ...form, subscriptionEndDate: endDate };
    if (form.subscriptionStatus === 'EXPIRED' && endDate && endDate > todayISO()) {
      const plan = subscriptionPlans.find((p) => String(p.id) === String(form.subscriptionPlanId));
      next.subscriptionStatus = plan?.isDefaultPlan ? 'TRIAL' : 'ACTIVE';
    }
    return next;
  };

  // Choosing another plan moves the status with it (the default plan is the trial; any other is a paying plan), unless the status was
  // already changed by hand: otherwise a customer moved to Enterprise stays marked Trial because the form starts with the old status
  const handlePlanChoice = (planId) => {
    setSubscriptionAssignForm((form) => {
      const next = { ...form, subscriptionPlanId: planId };
      const plan = subscriptionPlans.find((p) => String(p.id) === String(planId));
      const changed = String(planId) !== String(selectedOrgFull?.subscriptionPlan?.id || '');
      if (plan && changed && form.subscriptionStatus === (selectedOrgFull?.subscriptionStatus || '')) {
        next.subscriptionStatus = plan.isDefaultPlan ? 'TRIAL' : 'ACTIVE';
      } else if (plan && !changed) {
        next.subscriptionStatus = selectedOrgFull?.subscriptionStatus || ''; // back to the plan it has: its status too
      }
      return next;
    });
  };

  const handleSaveOrgSubscription = async (e) => {
    e.preventDefault();
    if (!selectedOrg) return;
    setSubscriptionAssignSaving(true);
    try {
      await axios.put(`/api/system-admin/organizations/${selectedOrg.id}/subscription`, {
        subscriptionPlanId: subscriptionAssignForm.subscriptionPlanId || null,
        subscriptionStatus: subscriptionAssignForm.subscriptionStatus || null,
        subscriptionStartDate: subscriptionAssignForm.subscriptionStartDate ? `${subscriptionAssignForm.subscriptionStartDate}T00:00:00` : null,
        subscriptionEndDate: subscriptionAssignForm.subscriptionEndDate ? `${subscriptionAssignForm.subscriptionEndDate}T00:00:00` : null
      });
      toast.success('Subscription updated successfully');
      fetchOrgDetails(selectedOrg.id);
    } catch (error) {
      const errorMsg = error.response?.data || 'Error updating subscription';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error updating subscription');
    } finally {
      setSubscriptionAssignSaving(false);
    }
  };

  const handleCreateOrganization = () => {
    setOrgFormData({
      name: '',
      email: '',
      description: '',
      phoneNumber: '',
      address: '',
      city: '',
      district: '',
      state: '',
      country: '',
      postalCode: '',
      website: '',
      defaultCurrency: 'USD',
      logoUrl: '',
      isActive: true
    });
    setSelectedOrgCountryCode('');
    setShowCreateOrgPanel(true);
  };

  const handleCloseOrgPanel = () => {
    setShowCreateOrgPanel(false);
    setOrgFormData({
      name: '',
      email: '',
      description: '',
      phoneNumber: '',
      address: '',
      city: '',
      district: '',
      state: '',
      country: '',
      postalCode: '',
      website: '',
      defaultCurrency: 'USD',
      logoUrl: '',
      isActive: true
    });
    setSelectedOrgCountryCode('');
  };

  const handleSaveOrganization = async (e) => {
    e.preventDefault();
    const website = checkWebAddress('Website', orgFormData.website);
    const logoUrl = checkWebAddress('Logo URL', orgFormData.logoUrl);
    const bad = [website, logoUrl].find((c) => c.error);
    if (bad) {
      toast.error(bad.error);
      return;
    }
    try {
      await axios.post('/api/system-admin/organizations', { ...orgFormData, website: website.value, logoUrl: logoUrl.value });
      toast.success('Organization created successfully');
      handleCloseOrgPanel();
      fetchOrganizations();
      if (activeTab === 'dashboard') {
        fetchDashboard();
      }
    } catch (error) {
      const errorMsg = error.response?.data || 'Error creating organization';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error creating organization');
    }
  };

  const handlePromoteUser = async () => {
    try {
      await axios.put(`/api/system-admin/users/${selectedUser.id}/promote`);
      toast.success('User promoted to system admin successfully');
      setShowPromoteModal(false);
      setSelectedUser(null);
      if (activeTab === 'system-admins') {
        fetchSystemAdmins();
      }
      if (selectedOrg) {
        fetchOrgDetails(selectedOrg.id);
      }
    } catch (error) {
      toast.error(error.response?.data || 'Error promoting user');
    }
  };

  const handleAssignOrgAdminRole = async () => {
    if (!selectedUser || !adminRoleId) {
      toast.error('Unable to assign role. Missing user or Org Admin role.');
      return;
    }

    try {
      const response = await axios.put(`/api/system-admin/users/${selectedUser.id}/assign-role/${adminRoleId}`);
      toast.success('Org Admin role assigned successfully');
      setShowAssignRoleModal(false);
      setSelectedUser(null);
      
      // Refresh user list
      if (selectedOrg) {
        fetchOrgDetails(selectedOrg.id);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data || 'Error assigning role';
      toast.error(errorMsg);
    }
  };

  const handleRemoveOrgAdminRole = async (userId, roleId) => {
    if (!window.confirm('Are you sure you want to remove Org Admin role from this user?')) {
      return;
    }

    try {
      await axios.delete(`/api/system-admin/users/${userId}/remove-role/${roleId}`);
      toast.success('Org Admin role removed successfully');
      
      // Refresh user list
      if (selectedOrg) {
        fetchOrgDetails(selectedOrg.id);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data || 'Error removing role';
      toast.error(errorMsg);
    }
  };

  const handleDemoteUser = async () => {
    try {
      await axios.put(`/api/system-admin/users/${selectedUser.id}/demote`);
      toast.success('User demoted from system admin successfully');
      setShowDemoteModal(false);
      setSelectedUser(null);
      if (activeTab === 'system-admins') {
        fetchSystemAdmins();
      }
      if (selectedOrg) {
        fetchOrgDetails(selectedOrg.id);
      }
    } catch (error) {
      toast.error(error.response?.data || 'Error demoting user');
    }
  };

  const orgUsersApiBase = () =>
    selectedOrg ? `/api/system-admin/organizations/${selectedOrg.id}/users` : null;
  const orgRolesApiBase = () =>
    selectedOrg ? `/api/system-admin/organizations/${selectedOrg.id}/roles` : null;

  const handleCreateUser = () => {
    setEditingUser(null);
    setUserFormData({
      email: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      password: '',
      isActive: true
    });
    setShowUserModal(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserFormData({
      email: user.email || '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      phoneNumber: user.phoneNumber || '',
      password: '',
      isActive: user.isActive !== false
    });
    setShowUserModal(true);
  };

  const handleCloseUserModal = () => {
    setShowUserModal(false);
    setEditingUser(null);
    setUserFormData({
      email: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      password: '',
      isActive: true
    });
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    if (!selectedOrg) return;
    if (userFormData.phoneNumber && !isValidPhoneNumber(userFormData.phoneNumber)) {
      toast.error('Phone number doesn\'t look valid');
      return;
    }
    if (!editingUser && userFormData.password) {
      const weak = passwordProblem(userFormData.password);
      if (weak) {
        toast.error(weak);
        return;
      }
    }
    try {
      if (editingUser) {
        const { password, ...updateData } = userFormData;
        await axios.put(`${orgUsersApiBase()}/${editingUser.id}`, updateData);
        toast.success('User updated successfully');
      } else {
        const response = await axios.post(orgUsersApiBase(), userFormData);
        const invited = response.data?.invited;
        toast.success(
          invited
            ? (response.data?.message || 'Existing user invited to this organization')
            : 'User created successfully'
        );
      }
      handleCloseUserModal();
      fetchOrgDetails(selectedOrg.id);
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.response?.data || 'Error saving user';
      toast.error(typeof errorMessage === 'string' ? errorMessage : 'Error saving user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!selectedOrg || !window.confirm('Are you sure you want to delete this user?')) {
      return;
    }
    try {
      await axios.delete(`${orgUsersApiBase()}/${userId}`);
      toast.success('User deleted successfully');
      fetchOrgDetails(selectedOrg.id);
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.response?.data || 'Error deleting user';
      toast.error(typeof errorMessage === 'string' ? errorMessage : 'Error deleting user');
    }
  };

  const handleManageUserRoles = async (user) => {
    if (!selectedOrg) return;
    try {
      const response = await axios.get(`${orgUsersApiBase()}/${user.id}`);
      setSelectedUserForRoleAssignment(response.data);
      setShowRoleAssignmentModal(true);
    } catch (error) {
      toast.error('Error loading user data');
      console.error(error);
    }
  };

  const handleCloseRoleAssignmentModal = () => {
    setShowRoleAssignmentModal(false);
    setSelectedUserForRoleAssignment(null);
  };

  const handleAssignRoleToUser = async (roleId) => {
    if (!selectedUserForRoleAssignment || !selectedOrg) return;
    try {
      await axios.put(`/api/system-admin/users/${selectedUserForRoleAssignment.id}/assign-role/${roleId}`);
      toast.success('Role assigned successfully');
      fetchOrgDetails(selectedOrg.id);
      const response = await axios.get(`${orgUsersApiBase()}/${selectedUserForRoleAssignment.id}`);
      setSelectedUserForRoleAssignment(response.data);
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data || 'Error assigning role';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error assigning role');
    }
  };

  const handleRemoveRoleFromUser = async (roleId) => {
    if (!selectedUserForRoleAssignment || !selectedOrg) return;
    if (!window.confirm('Are you sure you want to remove this role from the user?')) {
      return;
    }
    try {
      await axios.delete(`/api/system-admin/users/${selectedUserForRoleAssignment.id}/remove-role/${roleId}`);
      toast.success('Role removed successfully');
      fetchOrgDetails(selectedOrg.id);
      const response = await axios.get(`${orgUsersApiBase()}/${selectedUserForRoleAssignment.id}`);
      setSelectedUserForRoleAssignment(response.data);
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data || 'Error removing role';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error removing role');
    }
  };

  const handleCreateRole = () => {
    setSelectedRole(null);
    setRoleFormData({ name: '', description: '', isActive: true });
    setShowRoleModal(true);
  };

  const handleEditRole = (role) => {
    setSelectedRole(role);
    setRoleFormData({
      name: role.name || '',
      description: role.description || '',
      isActive: role.isActive !== false
    });
    setShowRoleModal(true);
  };

  const handleSaveRole = async (e) => {
    e.preventDefault();
    if (!selectedOrg) return;
    try {
      if (selectedRole) {
        await axios.put(`${orgRolesApiBase()}/${selectedRole.id}`, roleFormData);
        toast.success('Role updated successfully');
      } else {
        await axios.post(orgRolesApiBase(), roleFormData);
        toast.success('Role created successfully');
      }
      setShowRoleModal(false);
      setSelectedRole(null);
      fetchOrgDetails(selectedOrg.id);
    } catch (error) {
      const errorMsg = error.response?.data || 'Error saving role';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error saving role');
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!selectedOrg || !window.confirm('Are you sure you want to delete this role? This action cannot be undone.')) {
      return;
    }
    try {
      await axios.delete(`${orgRolesApiBase()}/${roleId}`);
      toast.success('Role deleted successfully');
      fetchOrgDetails(selectedOrg.id);
    } catch (error) {
      const errorMsg = error.response?.data || 'Error deleting role';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error deleting role');
    }
  };

  const fetchPermissionsGrouped = async () => {
    try {
      const response = await axios.get('/api/system-admin/permissions/grouped');
      setPermissionsGrouped(response.data || {});
    } catch (error) {
      toast.error('Error fetching permissions');
      console.error(error);
    }
  };

  const handleManagePermissions = async (role) => {
    if (!selectedOrg) return;
    setSelectedRole(role);
    try {
      await fetchPermissionsGrouped();
      const response = await axios.get(`${orgRolesApiBase()}/${role.id}/permissions`);
      setRolePermissions((response.data || []).map((p) => p.id));
      setShowPermissionModal(true);
    } catch (error) {
      toast.error('Error loading role permissions');
      console.error(error);
    }
  };

  const togglePermission = (permissionId) => {
    setRolePermissions((prev) =>
      prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const toggleResourcePermissions = (resource) => {
    const resourcePermissions = permissionsGrouped[resource] || [];
    const resourceIds = resourcePermissions.map((p) => p.id);
    const allSelected = resourceIds.every((id) => rolePermissions.includes(id));
    if (allSelected) {
      setRolePermissions((prev) => prev.filter((id) => !resourceIds.includes(id)));
    } else {
      setRolePermissions((prev) => {
        const newIds = resourceIds.filter((id) => !prev.includes(id));
        return [...prev, ...newIds];
      });
    }
  };

  const handleSavePermissions = async () => {
    if (!selectedOrg || !selectedRole) return;
    try {
      await axios.put(`${orgRolesApiBase()}/${selectedRole.id}/permissions`, rolePermissions);
      toast.success('Permissions updated successfully');
      setShowPermissionModal(false);
      fetchOrgDetails(selectedOrg.id);
    } catch (error) {
      const errorMsg = error.response?.data || 'Error updating permissions';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error updating permissions');
    }
  };

  // Only show loading spinner for dashboard tab when loading and no data yet
  if (loading && activeTab === 'dashboard' && !dashboard) {
    return (
      <Container className="mt-4">
        <div className="d-flex justify-content-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  return (
    <>
      <style>{`
        .slide-over-panel {
          position: fixed;
          top: 0;
          right: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.5);
          z-index: 1050;
          display: flex;
          justify-content: flex-end;
          opacity: 0;
          visibility: hidden;
          transition: opacity 0.3s ease, visibility 0.3s ease;
        }

        .slide-over-panel.show {
          opacity: 1;
          visibility: visible;
        }

        .slide-over-content {
          width: 600px;
          max-width: 90vw;
          height: 100%;
          background-color: var(--bs-body-bg);
          color: var(--bs-body-color);
          box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
          display: flex;
          flex-direction: column;
          transform: translateX(100%);
          transition: transform 0.3s ease;
        }

        .slide-over-panel.show .slide-over-content {
          transform: translateX(0);
        }

        .slide-over-header {
          padding: 1.5rem;
          border-bottom: 1px solid var(--bs-border-color);
          display: flex;
          justify-content: space-between;
          align-items: center;
          background-color: var(--bs-secondary-bg);
        }

        .slide-over-header h4 {
          margin: 0;
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--bs-emphasis-color);
        }

        .slide-over-body {
          flex: 1;
          overflow-y: auto;
          padding: 1.5rem;
          background-color: var(--bs-body-bg);
          color: var(--bs-body-color);
        }

        .slide-over-footer {
          padding: 1rem 1.5rem;
          border-top: 1px solid var(--bs-border-color);
          display: flex;
          justify-content: flex-end;
          gap: 0.5rem;
          background-color: var(--bs-secondary-bg);
        }

        /* Dark theme specific adjustments */
        [data-bs-theme="dark"] .slide-over-panel {
          background-color: rgba(0, 0, 0, 0.7);
        }

        [data-bs-theme="dark"] .slide-over-content {
          box-shadow: -2px 0 8px rgba(0, 0, 0, 0.5);
        }

        @media (max-width: 768px) {
          .slide-over-content {
            width: 100%;
            max-width: 100vw;
          }
        }
      `}</style>
      {versionInfo && (
        <div
          className="text-muted"
          style={{
            position: 'fixed', bottom: 8, right: 12, zIndex: 1040,
            fontSize: '0.7rem', lineHeight: 1.4, opacity: 0.6, pointerEvents: 'none'
          }}
          title={`Built ${versionInfo.buildTime ? formatDateTime(versionInfo.buildTime) : 'unknown time'} from branch ${versionInfo.branch || 'unknown'}`}
        >
          v{versionInfo.version || '?'} · {versionInfo.commitId || 'no commit info'}
          {versionInfo.buildTime ? ` · built ${formatDateTime(versionInfo.buildTime)} (your time)` : ''}
          {versionInfo.serverTimeZone ? ` · the server runs on ${versionInfo.serverTimeZone} time (UTC${versionInfo.serverUtcOffset === 'Z' ? '' : versionInfo.serverUtcOffset})` : ''}
        </div>
      )}
      <Container fluid className="mt-4 admin-page-shell">
        <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)} mountOnEnter>
        <div className="d-flex admin-tab-layout">
          <AdminSectionNav sections={visibleSections} activeKey={activeTab} onSelect={setActiveTab} ariaLabel={supportModule ? 'Tech Support sections' : 'System Admin sections'} />
          <div className="flex-grow-1 admin-tab-content-col">
            <Tab.Content>
            {/* Dashboard Tab */}
            <Tab.Pane eventKey="dashboard">
              {dashboard && (
                <Row xs={2} md={5} className="mt-3 g-3">
                  <Col>
                    <Card className="text-center h-100">
                      <Card.Body>
                        <h3>{dashboard.totalOrganizations}</h3>
                        <p className="text-muted mb-0">Organizations</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col>
                    <Card className="text-center h-100">
                      <Card.Body>
                        <h3>{dashboard.totalUsers}</h3>
                        <p className="text-muted mb-0">Total Users</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col>
                    <Card className="text-center h-100">
                      <Card.Body>
                        <h3>{dashboard.totalRoles}</h3>
                        <p className="text-muted mb-0">Total Roles</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col>
                    <Card className="text-center h-100">
                      <Card.Body>
                        <h3>{dashboard.totalSystemAdmins}</h3>
                        <p className="text-muted mb-0">System Admins</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col>
                    <Card className="text-center h-100">
                      <Card.Body>
                        <h3>{dashboard.activeUsers}</h3>
                        <p className="text-muted mb-0">Active Now</p>
                        <small className="text-muted">last 15 min</small>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}

              {supportModule ? <SupportTickets ticketsAddress="/tech-support/feedback" /> : <SalesFunnel />}

              {dashboard && dashboard.organizations && dashboard.organizations.length > 0 && (
                <Card className="mt-4">
                  <Card.Header>
                    <h5>Organizations Overview</h5>
                  </Card.Header>
                  <Card.Body>
                    <Table striped bordered hover>
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Name</th>
                          <th>Users</th>
                          <th>Active Users</th>
                          <th>Roles</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboard.organizations.map((org) => (
                          <tr key={org.id}>
                            <td>{org.id}</td>
                            <td>{org.name}</td>
                            <td>{org.userCount}</td>
                            <td>{org.activeUserCount}</td>
                            <td>{org.roleCount}</td>
                            <td>
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => {
                                  setSelectedOrg({ id: org.id, name: org.name });
                                  setActiveTab('organizations');
                                }}
                              >
                                View Details
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}
            </Tab.Pane>

            {/* Organizations Tab */}
            <Tab.Pane eventKey="organizations">
              {supportModule ? (
                <SupportOrganizations organizations={organizations} selected={selectedOrg} onSelect={setSelectedOrg} />
              ) : (
              <>
              <div className="mt-3">
                {selectedOrg ? (
                  <div>
                    <Button variant="outline-secondary" className="mb-3" onClick={() => setSelectedOrg(null)}>
                      ← Back to Organizations
                    </Button>
                    <Card>
                      <Card.Header>
                        <h5>{selectedOrg.name} - Details</h5>
                      </Card.Header>
                      <Card.Body>
                        <Tabs defaultActiveKey="users">
                          <Tab eventKey="users" title="Users">
                            <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
                              <p className="text-muted mb-0">Manage users in this organization</p>
                              <Button variant="primary" onClick={handleCreateUser}>
                                + Create User
                              </Button>
                            </div>
                            <Table striped bordered hover responsive>
                              <thead>
                                <tr>
                                  <th>Email</th>
                                  <th>Name</th>
                                  <th>Roles</th>
                                  <th>Status</th>
                                  <th>System Admin</th>
                                  <th>Last login</th>
                                  <th>Last active</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {orgUsers.length === 0 ? (
                                  <tr>
                                    <td colSpan="8" className="text-center text-muted">
                                      No users found. Create a user to get started.
                                    </td>
                                  </tr>
                                ) : (
                                  orgUsers.map((user) => {
                                  const hasAdminRole = user.roles && user.roles.some(r => {
                                    const roleName = typeof r === 'string' ? r : (r.name || '');
                                    return isOrgAdminRole(roleName);
                                  });
                                  const adminRole = user.roles && user.roles.find(r => {
                                    const roleName = typeof r === 'string' ? r : (r.name || '');
                                    return isOrgAdminRole(roleName);
                                  });
                                  const adminRoleIdFromUser = adminRole && typeof adminRole === 'object' && adminRole.id ? adminRole.id : null;
                                  // Use adminRoleId from organization if user doesn't have it yet
                                  const effectiveAdminRoleId = adminRoleIdFromUser || adminRoleId;
                                  
                                  return (
                                    <tr key={user.id}>
                                      <td>{user.email}</td>
                                      <td>{user.firstName} {user.lastName}</td>
                                      <td>
                                        {user.roles && user.roles.length > 0 ? (
                                          user.roles.map((role, idx) => {
                                            const roleName = typeof role === 'string' 
                                              ? role 
                                              : (role.name || (typeof role === 'object' && role.id ? `Role ${role.id}` : 'Unknown'));
                                            return (
                                              <Badge 
                                                key={idx} 
                                                bg={isOrgAdminRole(roleName) ? 'primary' : 'secondary'} 
                                                className="me-1"
                                              >
                                                {getRoleLabel(typeof role === 'object' ? role : roleName)}
                                              </Badge>
                                            );
                                          })
                                        ) : (
                                          <span className="text-muted">No roles</span>
                                        )}
                                      </td>
                                      <td>
                                        {user.isActive !== false ? (
                                          <Badge bg="success">Active</Badge>
                                        ) : (
                                          <Badge bg="danger">Inactive</Badge>
                                        )}
                                      </td>
                                      <td>
                                        {user.isSystemAdmin ? (
                                          <Badge bg="danger">Yes</Badge>
                                        ) : (
                                          <Badge bg="secondary">No</Badge>
                                        )}
                                      </td>
                                      <td>{user.lastLogin ? formatDateTime(user.lastLogin) : <span className="text-muted">Never</span>}</td>
                                      <td>{user.lastActiveAt ? formatDateTime(user.lastActiveAt) : <span className="text-muted">Never</span>}</td>
                                      <td>
                                        <div className="d-flex gap-1 flex-nowrap">
                                          <IconAction icon="person-gear" label="Manage roles" variant="outline-info" onClick={() => handleManageUserRoles(user)} />
                                          <IconAction icon="pencil" label="Edit user" variant="outline-primary" onClick={() => handleEditUser(user)} />
                                          <IconAction icon="trash" label="Delete user" variant="outline-danger" onClick={() => handleDeleteUser(user.id)} />
                                          {!hasAdminRole && effectiveAdminRoleId && (
                                            <IconAction
                                              icon="shield-plus" label="Make Org Admin" variant="outline-primary"
                                              onClick={() => {
                                                setSelectedUser(user);
                                                setShowAssignRoleModal(true);
                                              }}
                                            />
                                          )}
                                          {hasAdminRole && effectiveAdminRoleId && (
                                            <IconAction icon="shield-minus" label="Remove Org Admin" variant="outline-danger" onClick={() => handleRemoveOrgAdminRole(user.id, effectiveAdminRoleId)} />
                                          )}
                                          {!user.isSystemAdmin ? (
                                            <IconAction
                                              icon="arrow-up-circle" label="Promote to System Admin" variant="outline-success"
                                              onClick={() => {
                                                setSelectedUser(user);
                                                setShowPromoteModal(true);
                                              }}
                                            />
                                          ) : (
                                            <IconAction
                                              icon="arrow-down-circle" label="Demote from System Admin" variant="outline-warning"
                                              onClick={() => {
                                                setSelectedUser(user);
                                                setShowDemoteModal(true);
                                              }}
                                            />
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })
                                )}
                              </tbody>
                            </Table>
                          </Tab>
                          <Tab eventKey="roles" title="Roles">
                            <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
                              <p className="text-muted mb-0">Manage roles and permissions for this organization</p>
                              <Button variant="primary" onClick={handleCreateRole}>
                                + Create Role
                              </Button>
                            </div>
                            <Table striped bordered hover>
                              <thead>
                                <tr>
                                  <th>Name</th>
                                  <th>Description</th>
                                  <th>System Role</th>
                                  <th>Active</th>
                                  <th>Permissions</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {orgRoles.length === 0 ? (
                                  <tr>
                                    <td colSpan="6" className="text-center text-muted">
                                      No roles found. Create a role to get started.
                                    </td>
                                  </tr>
                                ) : (
                                  orgRoles.map((role) => (
                                  <tr key={role.id}>
                                    <td><strong>{getRoleLabel(role)}</strong></td>
                                    <td>{role.description || '-'}</td>
                                    <td>
                                      {role.isSystemRole ? (
                                        <Badge bg="info">System</Badge>
                                      ) : (
                                        <Badge bg="secondary">Custom</Badge>
                                      )}
                                    </td>
                                    <td>
                                      {role.isActive ? (
                                        <Badge bg="success">Active</Badge>
                                      ) : (
                                        <Badge bg="danger">Inactive</Badge>
                                      )}
                                    </td>
                                    <td>
                                      {role.permissions ? `${role.permissions.length} permission(s)` : '0 permission(s)'}
                                    </td>
                                    <td>
                                      <div className="d-flex gap-2 flex-wrap">
                                        <Button
                                          size="sm"
                                          variant="outline-primary"
                                          onClick={() => handleManagePermissions(role)}
                                          title="Manage Permissions"
                                        >
                                          Permissions
                                        </Button>
                                        {!role.isSystemRole && (
                                          <>
                                            <Button
                                              size="sm"
                                              variant="outline-secondary"
                                              onClick={() => handleEditRole(role)}
                                              title="Edit Role"
                                            >
                                              Edit
                                            </Button>
                                            <Button
                                              size="sm"
                                              variant="outline-danger"
                                              onClick={() => handleDeleteRole(role.id)}
                                              title="Delete Role"
                                            >
                                              Delete
                                            </Button>
                                          </>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))
                                )}
                              </tbody>
                            </Table>
                          </Tab>
                          <Tab eventKey="subscription" title="Subscription">
                            <Row className="mt-3">
                            <Col md={7}>
                              <Card className="mb-3">
                                <Card.Body>
                                  <div className="d-flex justify-content-between align-items-start mb-3">
                                    <div>
                                      <div className="text-muted small">Current Plan</div>
                                      <h5 className="mb-0">{selectedOrgFull?.subscriptionPlan?.name || 'No plan assigned (unlimited)'}</h5>
                                    </div>
                                    <Badge bg={
                                      !selectedOrgFull?.subscriptionPlan ? 'secondary' :
                                      selectedOrgFull?.subscriptionStatus === 'ACTIVE' ? 'success' :
                                      selectedOrgFull?.subscriptionStatus === 'TRIAL' ? 'info' :
                                      selectedOrgFull?.subscriptionStatus === 'EXPIRED' ? 'danger' : 'secondary'
                                    }>
                                      {selectedOrgFull?.subscriptionPlan ? (selectedOrgFull?.subscriptionStatus || 'N/A') : 'No Plan'}
                                    </Badge>
                                  </div>
                                  {(selectedOrgFull?.subscriptionStartDate || selectedOrgFull?.subscriptionEndDate) && (
                                    <p className="text-muted small mb-3">
                                      {selectedOrgFull?.subscriptionStartDate && (
                                        <>Plan start: {formatDate(selectedOrgFull.subscriptionStartDate)}</>
                                      )}
                                      {selectedOrgFull?.subscriptionStartDate && selectedOrgFull?.subscriptionEndDate && '  ·  '}
                                      {selectedOrgFull?.subscriptionEndDate && (
                                        <>Plan end: {formatDate(selectedOrgFull.subscriptionEndDate)}</>
                                      )}
                                    </p>
                                  )}
                                  {orgSubscriptionUsage && (
                                    <Table size="sm" bordered className="mb-0">
                                      <tbody>
                                        <tr>
                                          <td>Warehouses in use</td>
                                          <td className="text-end">
                                            {orgSubscriptionUsage.warehouseCount}
                                            {selectedOrgFull?.subscriptionPlan?.maxWarehouses != null ? ` of ${selectedOrgFull.subscriptionPlan.maxWarehouses}` : ''}
                                          </td>
                                        </tr>
                                        <tr>
                                          <td>Production units in use</td>
                                          <td className="text-end">
                                            {orgSubscriptionUsage.productionUnitCount}
                                            {selectedOrgFull?.subscriptionPlan?.maxProductionUnits != null ? ` of ${selectedOrgFull.subscriptionPlan.maxProductionUnits}` : ''}
                                          </td>
                                        </tr>
                                        <tr>
                                          <td>Retail stores in use</td>
                                          <td className="text-end">
                                            {orgSubscriptionUsage.retailStoreCount}
                                            {selectedOrgFull?.subscriptionPlan?.maxRetailStores != null ? ` of ${selectedOrgFull.subscriptionPlan.maxRetailStores}` : ''}
                                          </td>
                                        </tr>
                                        <tr>
                                          <td>Transactions today</td>
                                          <td className="text-end">
                                            {orgSubscriptionUsage.todayTransactionCount}
                                            {selectedOrgFull?.subscriptionPlan?.maxDailyTransactions != null ? ` of ${selectedOrgFull.subscriptionPlan.maxDailyTransactions}` : ''}
                                          </td>
                                        </tr>
                                        <tr>
                                          <td>User licenses in use</td>
                                          <td className="text-end">
                                            {orgSubscriptionUsage.activeUserCount}
                                            {selectedOrgFull?.subscriptionPlan?.maxUsers != null ? ` of ${selectedOrgFull.subscriptionPlan.maxUsers}` : ''}
                                          </td>
                                        </tr>
                                      </tbody>
                                    </Table>
                                  )}
                                </Card.Body>
                              </Card>

                              <Card>
                                <Card.Header>
                                  <h6 className="mb-0">Assign / Change Plan</h6>
                                </Card.Header>
                                <Card.Body>
                                  <Form onSubmit={handleSaveOrgSubscription}>
                                    <Form.Group className="mb-3">
                                      <Form.Label>Plan</Form.Label>
                                      <Form.Select
                                        value={subscriptionAssignForm.subscriptionPlanId}
                                        onChange={(e) => handlePlanChoice(e.target.value)}
                                      >
                                        <option value="">-- No plan (unlimited) --</option>
                                        {subscriptionPlans.map((plan) => (
                                          <option key={plan.id} value={plan.id}>{plan.name}</option>
                                        ))}
                                      </Form.Select>
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                      <Form.Label>Status</Form.Label>
                                      <Form.Select
                                        value={subscriptionAssignForm.subscriptionStatus}
                                        onChange={(e) => setSubscriptionAssignForm({ ...subscriptionAssignForm, subscriptionStatus: e.target.value })}
                                      >
                                        <option value="">-- Unchanged --</option>
                                        <option value="TRIAL">Trial</option>
                                        <option value="ACTIVE">Active</option>
                                        <option value="EXPIRED">Expired</option>
                                      </Form.Select>
                                      <Form.Text className="text-muted">
                                        Active is a paying customer; Trial is a customer on trial until the end date.
                                      </Form.Text>
                                      {subscriptionAssignForm.subscriptionStatus === 'TRIAL'
                                        && subscriptionPlans.find((p) => String(p.id) === String(subscriptionAssignForm.subscriptionPlanId))?.isDefaultPlan === false && (
                                        <div className="small text-warning mt-1" data-testid="plan-status-warning">
                                          This is a paid plan but the status is Trial. Choose Active once the customer has paid.
                                        </div>
                                      )}
                                    </Form.Group>
                                    <Row>
                                      <Col>
                                        <Form.Group className="mb-3">
                                          <Form.Label>Plan Start Date</Form.Label>
                                          <DateInput
                                           
                                            value={subscriptionAssignForm.subscriptionStartDate}
                                            onChange={(e) => setSubscriptionAssignForm({ ...subscriptionAssignForm, subscriptionStartDate: e.target.value })}
                                          />
                                        </Form.Group>
                                      </Col>
                                      <Col>
                                        <Form.Group className="mb-3">
                                          <Form.Label>Plan End Date</Form.Label>
                                          <DateInput
                                           
                                            value={subscriptionAssignForm.subscriptionEndDate}
                                            onChange={(e) => setSubscriptionAssignForm(withEndDate(subscriptionAssignForm, e.target.value))}
                                          />
                                          <div className="mt-1">
                                            <Button
                                              size="sm"
                                              variant="outline-secondary"
                                              className="me-1"
                                              onClick={() => setSubscriptionAssignForm(withEndDate(subscriptionAssignForm, addToDateString(subscriptionAssignForm.subscriptionStartDate, 1)))}
                                            >
                                              +1 Month
                                            </Button>
                                            <Button
                                              size="sm"
                                              variant="outline-secondary"
                                              onClick={() => setSubscriptionAssignForm(withEndDate(subscriptionAssignForm, addToDateString(subscriptionAssignForm.subscriptionStartDate, 12)))}
                                            >
                                              +1 Year
                                            </Button>
                                          </div>
                                        </Form.Group>
                                      </Col>
                                    </Row>
                                    <Button type="submit" variant="primary" disabled={subscriptionAssignSaving}>
                                      {subscriptionAssignSaving ? 'Saving...' : 'Save'}
                                    </Button>
                                  </Form>
                                </Card.Body>
                              </Card>
                            </Col>
                            <Col md={5}>
                              <Card>
                                <Card.Header>
                                  <h6 className="mb-0">Plan History</h6>
                                </Card.Header>
                                <Card.Body style={{ maxHeight: '520px', overflowY: 'auto' }}>
                                  {orgSubscriptionHistory.length === 0 ? (
                                    <p className="text-muted small mb-0">No changes yet.</p>
                                  ) : (
                                    orgSubscriptionHistory.map((entry, idx) => (
                                      <div key={entry.id} className={idx > 0 ? 'pt-2 mt-2 border-top' : ''}>
                                        <div className="small text-muted">
                                          {entry.changedAt ? formatDateTime(entry.changedAt) : ''}
                                        </div>
                                        <div className="small">
                                          Plan: <strong>{entry.fromPlan?.name || 'No plan'}</strong> &rarr; <strong>{entry.toPlan?.name || 'No plan'}</strong>
                                        </div>
                                        <div className="small">
                                          Status: {entry.fromStatus || '—'} &rarr; {entry.toStatus || '—'}
                                        </div>
                                        <div className="small text-muted">
                                          by {entry.changedByName || entry.changedByEmail}
                                        </div>
                                      </div>
                                    ))
                                  )}
                                </Card.Body>
                              </Card>
                            </Col>
                            </Row>
                          </Tab>
                          <Tab eventKey="partner" title="Account Manager">
                            <Row className="mt-3">
                              <Col md={6}>
                                <Card className="mb-3">
                                  <Card.Header>
                                    <h6 className="mb-0">Active Account Managers</h6>
                                  </Card.Header>
                                  <Card.Body>
                                    {orgPartnerAssignments.length === 0 ? (
                                      <p className="text-muted small mb-0">No Account Manager assigned - this organization is served directly.</p>
                                    ) : (
                                      orgPartnerAssignments.map((a) => (
                                        <div key={a.id} className="d-flex justify-content-between align-items-start border rounded p-2 mb-2">
                                          <div>
                                            <p className="mb-1">
                                              <strong>{a.partner?.name}</strong>
                                              {a.partner?.user && <Badge bg="info" className="ms-2">Has login</Badge>}
                                            </p>
                                            <p className="text-muted small mb-1">
                                              {a.initialRatePercent}% for the first {a.initialPeriodMonths} month(s),
                                              then {a.trailingRatePercent}% ongoing
                                              {a.capAmount != null ? ` · capped at ${a.capAmount}` : ''}
                                              {a.capMonths != null ? ` · commission stops after ${a.capMonths} month(s)` : ''}
                                            </p>
                                            <p className="text-muted small mb-0">
                                              Since {a.startDate ? formatDate(a.startDate) : ''}
                                            </p>
                                          </div>
                                          <div className="d-flex flex-column gap-1">
                                            <Button variant="outline-primary" size="sm" onClick={() => setEditingAssignment(a)}>
                                              Edit terms
                                            </Button>
                                            <Button variant="outline-danger" size="sm" onClick={() => handleEndPartnerAssignment(a.id)}>
                                              End
                                            </Button>
                                          </div>
                                        </div>
                                      ))
                                    )}
                                  </Card.Body>
                                </Card>

                                <Card>
                                  <Card.Header>
                                    <h6 className="mb-0">Add Account Manager</h6>
                                  </Card.Header>
                                  <Card.Body>
                                    <Form onSubmit={handleSavePartnerAssignment}>
                                      <Form.Group className="mb-3">
                                        <Form.Label>Account Manager</Form.Label>
                                        <Form.Select
                                          value={partnerAssignForm.partnerId}
                                          onChange={(e) => setPartnerAssignForm({ ...partnerAssignForm, partnerId: e.target.value })}
                                          required
                                        >
                                          <option value="">-- Choose an Account Manager --</option>
                                          {partners
                                            .filter((p) => !orgPartnerAssignments.some((a) => String(a.partner?.id) === String(p.id)))
                                            .map((p) => (
                                              <option key={p.id} value={p.id}>{p.name}{p.user ? ' (has login)' : ''}</option>
                                            ))}
                                        </Form.Select>
                                      </Form.Group>
                                      {partnerAssignForm.partnerId && !partners.find((p) => String(p.id) === String(partnerAssignForm.partnerId))?.user && (
                                        <Form.Check
                                          type="checkbox"
                                          id="partner-create-login"
                                          className="mb-3"
                                          label="Send this Account Manager a login invite (they'll see this organization's support tickets)"
                                          checked={partnerAssignForm.createLogin}
                                          onChange={(e) => setPartnerAssignForm({ ...partnerAssignForm, createLogin: e.target.checked })}
                                        />
                                      )}
                                      <Row>
                                        <Col>
                                          <Form.Group className="mb-3">
                                            <Form.Label>Initial rate %</Form.Label>
                                            <Form.Control
                                              type="number" step="0.01" min="0" required
                                              value={partnerAssignForm.initialRatePercent}
                                              onChange={(e) => setPartnerAssignForm({ ...partnerAssignForm, initialRatePercent: e.target.value })}
                                            />
                                          </Form.Group>
                                        </Col>
                                        <Col>
                                          <Form.Group className="mb-3">
                                            <Form.Label>For (months)</Form.Label>
                                            <Form.Control
                                              type="number" step="1" min="0" required
                                              value={partnerAssignForm.initialPeriodMonths}
                                              onChange={(e) => setPartnerAssignForm({ ...partnerAssignForm, initialPeriodMonths: e.target.value })}
                                            />
                                          </Form.Group>
                                        </Col>
                                      </Row>
                                      <Form.Group className="mb-3">
                                        <Form.Label>Trailing rate % (after that)</Form.Label>
                                        <Form.Control
                                          type="number" step="0.01" min="0" required
                                          value={partnerAssignForm.trailingRatePercent}
                                          onChange={(e) => setPartnerAssignForm({ ...partnerAssignForm, trailingRatePercent: e.target.value })}
                                        />
                                      </Form.Group>
                                      <Row>
                                        <Col>
                                          <Form.Group className="mb-3">
                                            <Form.Label>Cap amount (optional)</Form.Label>
                                            <Form.Control
                                              type="number" step="0.01" min="0"
                                              value={partnerAssignForm.capAmount}
                                              onChange={(e) => setPartnerAssignForm({ ...partnerAssignForm, capAmount: e.target.value })}
                                            />
                                          </Form.Group>
                                        </Col>
                                        <Col>
                                          <Form.Group className="mb-3">
                                            <Form.Label>Cap duration, months (optional)</Form.Label>
                                            <Form.Control
                                              type="number" step="1" min="0"
                                              value={partnerAssignForm.capMonths}
                                              onChange={(e) => setPartnerAssignForm({ ...partnerAssignForm, capMonths: e.target.value })}
                                            />
                                          </Form.Group>
                                        </Col>
                                      </Row>
                                      <Form.Group className="mb-3">
                                        <Form.Label>Start date</Form.Label>
                                        <DateInput
                                          required
                                          value={partnerAssignForm.startDate}
                                          onChange={(e) => setPartnerAssignForm({ ...partnerAssignForm, startDate: e.target.value })}
                                        />
                                      </Form.Group>
                                      <Button type="submit" variant="primary" disabled={partnerAssignSaving}>
                                        {partnerAssignSaving ? 'Saving...' : 'Save'}
                                      </Button>
                                    </Form>
                                  </Card.Body>
                                </Card>
                              </Col>
                            </Row>
                          </Tab>

                          <Tab eventKey="usage-metrics" title="Usage Metrics">
                            <Row className="mt-3">
                              <Col md={6}>
                                <Card className="mb-3">
                                  <Card.Header>
                                    <h6 className="mb-0">Setup &amp; master data</h6>
                                  </Card.Header>
                                  <Card.Body>
                                    {orgUsageMetrics && (
                                      <Table size="sm" bordered className="mb-0">
                                        <tbody>
                                          <tr>
                                            <td>Warehouses</td>
                                            <td className="text-end">{orgUsageMetrics.warehouseCount}</td>
                                          </tr>
                                          <tr>
                                            <td>Production units</td>
                                            <td className="text-end">{orgUsageMetrics.productionUnitCount}</td>
                                          </tr>
                                          <tr>
                                            <td>Retail stores</td>
                                            <td className="text-end">{orgUsageMetrics.retailStoreCount}</td>
                                          </tr>
                                          <tr>
                                            <td>Users</td>
                                            <td className="text-end">{orgUsageMetrics.activeUserCount}</td>
                                          </tr>
                                          <tr>
                                            <td>Products</td>
                                            <td className="text-end">{orgUsageMetrics.productCount}</td>
                                          </tr>
                                          <tr>
                                            <td>Materials</td>
                                            <td className="text-end">{orgUsageMetrics.materialCount}</td>
                                          </tr>
                                          <tr>
                                            <td>Services</td>
                                            <td className="text-end">{orgUsageMetrics.serviceCount}</td>
                                          </tr>
                                        </tbody>
                                      </Table>
                                    )}
                                  </Card.Body>
                                </Card>
                              </Col>
                              <Col md={6}>
                                <Card>
                                  <Card.Header>
                                    <h6 className="mb-0">Activity &amp; recency</h6>
                                  </Card.Header>
                                  <Card.Body>
                                    {orgUsageMetrics && (
                                      <Table size="sm" bordered className="mb-0">
                                        <tbody>
                                          <tr>
                                            <td>Total GRNs</td>
                                            <td className="text-end">{orgUsageMetrics.grnCount}</td>
                                          </tr>
                                          <tr>
                                            <td>Total GINs</td>
                                            <td className="text-end">{orgUsageMetrics.ginCount}</td>
                                          </tr>
                                          <tr>
                                            <td>Transactions today</td>
                                            <td className="text-end">{orgUsageMetrics.todayTransactionCount}</td>
                                          </tr>
                                          <tr>
                                            <td>Last login (any member)</td>
                                            <td className="text-end">
                                              {orgUsageMetrics.lastLoginAt ? formatDateTime(orgUsageMetrics.lastLoginAt) : 'Never'}
                                            </td>
                                          </tr>
                                          <tr>
                                            <td>Last active (any member)</td>
                                            <td className="text-end">
                                              {orgUsageMetrics.lastActiveAt ? formatDateTime(orgUsageMetrics.lastActiveAt) : 'Never'}
                                            </td>
                                          </tr>
                                          <tr>
                                            <td>Last GRN date</td>
                                            <td className="text-end">
                                              {orgUsageMetrics.lastGrnDate ? formatDate(`${orgUsageMetrics.lastGrnDate}T00:00:00`) : 'Never'}
                                            </td>
                                          </tr>
                                          <tr>
                                            <td>Last GIN date</td>
                                            <td className="text-end">
                                              {orgUsageMetrics.lastGinDate ? formatDate(`${orgUsageMetrics.lastGinDate}T00:00:00`) : 'Never'}
                                            </td>
                                          </tr>
                                        </tbody>
                                      </Table>
                                    )}
                                  </Card.Body>
                                </Card>
                              </Col>
                            </Row>
                          </Tab>
                          <Tab eventKey="role-history" title="Role history">
                            <Card className="mt-3">
                              <Card.Body>
                                <p className="text-muted small">
                                  Every role given or taken in this organization (newest first, up to 200), with who did it.
                                </p>
                                {orgRoleChanges.length === 0 ? (
                                  <p className="text-muted small mb-0">No role changes recorded yet. Recording started with this release.</p>
                                ) : (
                                  <Table size="sm" responsive hover className="mb-0">
                                    <thead>
                                      <tr><th>When</th><th>User</th><th>Role</th><th>Change</th><th>How</th><th>By</th></tr>
                                    </thead>
                                    <tbody>
                                      {orgRoleChanges.map((c) => (
                                        <tr key={c.id}>
                                          <td className="small text-nowrap">{c.changedAt ? formatDateTime(c.changedAt) : ''}</td>
                                          <td className="small">{c.userEmail}</td>
                                          <td className="small">{getRoleLabel(c.roleName)}</td>
                                          <td>
                                            <Badge bg={c.action === 'ADDED' ? 'success' : 'danger'}>{c.action === 'ADDED' ? 'Given' : 'Taken'}</Badge>
                                          </td>
                                          <td className="small">{ROLE_CHANGE_REASONS[c.reason] || c.reason}</td>
                                          <td className="small">{c.changedBy === 'system' ? 'iBigDo (automatic)' : c.changedBy}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </Table>
                                )}
                              </Card.Body>
                            </Card>
                          </Tab>
                        </Tabs>
                      </Card.Body>
                    </Card>
                  </div>
                ) : (
                  <OrganizationsTable
                    organizations={organizations}
                    openLabel="View Details"
                    onOpen={(org) => setSelectedOrg({ id: org.id, name: org.name })}
                    headerRight={<Button variant="primary" onClick={() => setShowCreateOrgPanel(true)}>+ Create Organization</Button>}
                  />
                )}
              </div>
            </>
              )}
            </Tab.Pane>

            {/* System Admins Tab */}
            <Tab.Pane eventKey="system-admins">
              <div className="mt-3">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Name</th>
                      <th>Organization</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {systemAdmins.map((admin) => (
                      <tr key={admin.id}>
                        <td>{admin.email}</td>
                        <td>{admin.firstName} {admin.lastName}</td>
                        <td>{admin.organization?.name || 'N/A'}</td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-warning"
                            onClick={() => {
                              setSelectedUser(admin);
                              setShowDemoteModal(true);
                            }}
                          >
                            Demote
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                <Card className="mt-4">
                  <Card.Header><h5 className="mb-0">Tech Support</h5></Card.Header>
                  <Card.Body>
                    <p className="text-muted small">
                      iBigDo staff who answer customers: feedback, assistant questions, a look at an organization, the Global Material Master&apos;s
                      review and a set-password link for a locked-out login. They cannot change settings, plans, users or roles.
                    </p>
                    <Table striped bordered hover size="sm">
                      <thead><tr><th>Email</th><th>Name</th><th>Organization</th><th>Actions</th></tr></thead>
                      <tbody>
                        {techSupport.length === 0 && <tr><td colSpan={4} className="text-center text-muted">Nobody has Tech Support access yet</td></tr>}
                        {techSupport.map((person) => (
                          <tr key={person.id}>
                            <td>{person.email}</td>
                            <td>{person.firstName} {person.lastName}</td>
                            <td>{person.organization?.name || 'N/A'}</td>
                            <td><Button size="sm" variant="outline-warning" onClick={() => takeTechSupport(person)}>Take away</Button></td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    <h6 className="mt-3">Give Tech Support access</h6>
                    <div className="d-flex flex-wrap gap-2 align-items-end">
                      <Form.Group>
                        <Form.Label className="small mb-1">Organization</Form.Label>
                        <Form.Select size="sm" value={techOrgId} onChange={(e) => chooseTechOrg(e.target.value)}>
                          <option value="">Choose…</option>
                          {organizations.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                        </Form.Select>
                      </Form.Group>
                      <Form.Group>
                        <Form.Label className="small mb-1">Person</Form.Label>
                        <Form.Select size="sm" value={techUserId} onChange={(e) => setTechUserId(e.target.value)} disabled={!techOrgId}>
                          <option value="">Choose…</option>
                          {techOrgUsers.map((u) => <option key={u.id} value={u.id}>{u.firstName} {u.lastName} ({u.email})</option>)}
                        </Form.Select>
                      </Form.Group>
                      <Button size="sm" onClick={giveTechSupport} disabled={!techUserId}>Give access</Button>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            </Tab.Pane>

            {/* Units of Measurement Tab */}
            <Tab.Pane eventKey="uom">
              <UOMManagement />
            </Tab.Pane>

            {/* Material Master Tab */}
            <Tab.Pane eventKey="material-master">
              <MaterialMaster />
            </Tab.Pane>

            {/* Services Master Tab */}
            <Tab.Pane eventKey="services-master">
              <ServicesMaster />
            </Tab.Pane>

            {/* Goods Movement Types Tab */}
            <Tab.Pane eventKey="goods-movement-types">
              <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
                <div className="d-flex align-items-center gap-3">
                  <p className="text-muted mb-0">Manage system-wide goods movement types (e.g., Receive from Vendor, Return to Vendor)</p>
                  <Form.Select
                    style={{ width: 'auto' }}
                    value={movementTypeFilter}
                    onChange={(e) => setMovementTypeFilter(e.target.value)}
                  >
                    <option value="ALL">All</option>
                    <option value="INBOUND">Inbound</option>
                    <option value="OUTBOUND">Outbound</option>
                  </Form.Select>
                </div>
                <Button variant="primary" onClick={handleCreateMovementType}>
                  + Add Movement Type
                </Button>
              </div>

              {loading ? (
                <div className="d-flex justify-content-center">
                  <Spinner animation="border" role="status" />
                </div>
              ) : (
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">Goods Movement Types</h5>
                  </Card.Header>
                  <Card.Body>
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>Code</th>
                          <th>Name</th>
                          <th>Type</th>
                          <th>Reference Type</th>
                          <th>Location Type</th>
                          <th>Description</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {goodsMovementTypes.length === 0 ? (
                          <tr>
                            <td colSpan="8" className="text-center text-muted">
                              No goods movement types found. Create your first type to get started.
                            </td>
                          </tr>
                        ) : (
                          goodsMovementTypes
                            .filter(mt => movementTypeFilter === 'ALL' || mt.direction === movementTypeFilter)
                            .map((mt) => (
                              <tr key={mt.id}>
                                <td><strong>{mt.code}</strong></td>
                                <td>{mt.name}</td>
                                <td>
                                  <Badge bg={getMovementDirectionBadge(mt.direction)}>
                                    {mt.direction}
                                  </Badge>
                                </td>
                                <td>
                                  {mt.referenceType ? (
                                    <Badge bg="info">{mt.referenceType}</Badge>
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>
                                <td>
                                  {mt.locationType ? (
                                    <Badge bg="secondary">{mt.locationType}</Badge>
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>
                                <td>{mt.description || '-'}</td>
                                <td>
                                  <Badge bg={mt.isActive ? 'success' : 'secondary'}>
                                    {mt.isActive ? 'Active' : 'Inactive'}
                                  </Badge>
                                </td>
                                <td>
                                  <Button
                                    size="sm"
                                    variant="outline-info"
                                    className="me-2"
                                    onClick={() => handleViewPrint(mt, 'MOVEMENT_TYPE')}
                                    title="View & Print"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                      <path d="M5 1a2 2 0 0 0-2 2v1h10V3a2 2 0 0 0-2-2H5zm6 8H5a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1z"/>
                                      <path d="M0 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-1v-2a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2H2a2 2 0 0 1-2-2V7zm2.5 1a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z"/>
                                    </svg>
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline-primary"
                                    className="me-2"
                                    onClick={() => handleEditMovementType(mt)}
                                    title="Edit"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                      <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                                      <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
                                    </svg>
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    onClick={() => handleDeleteMovementType(mt.id)}
                                    title="Delete"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                      <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                      <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                    </svg>
                                  </Button>
                                </td>
                              </tr>
                            ))
                        )}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}
            </Tab.Pane>

            {/* Tax Rates Tab */}
            <Tab.Pane eventKey="tax-rates">
              <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
                <p className="text-muted mb-0">Manage system-wide GST rate slabs used on Sale Bill line items</p>
                <Button variant="primary" onClick={handleCreateTaxRate}>
                  + Add Tax Rate
                </Button>
              </div>

              {loading ? (
                <div className="d-flex justify-content-center">
                  <Spinner animation="border" role="status" />
                </div>
              ) : (
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">Tax Rates</h5>
                  </Card.Header>
                  <Card.Body>
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Rate %</th>
                          <th>Description</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {taxRates.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="text-center text-muted">
                              No tax rates found. Create your first tax rate to get started.
                            </td>
                          </tr>
                        ) : (
                          taxRates.map((rate) => (
                            <tr key={rate.id}>
                              <td><strong>{rate.name}</strong></td>
                              <td>{rate.ratePercent}%</td>
                              <td>{rate.description || '-'}</td>
                              <td>
                                <Badge bg={rate.isActive ? 'success' : 'secondary'}>
                                  {rate.isActive ? 'Active' : 'Inactive'}
                                </Badge>
                              </td>
                              <td>
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  className="me-2"
                                  onClick={() => handleEditTaxRate(rate)}
                                  title="Edit"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                                    <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
                                  </svg>
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => handleDeleteTaxRate(rate.id)}
                                  title="Delete"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                    <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                  </svg>
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}
            </Tab.Pane>

            {/* Subscription Plans Tab */}
            <Tab.Pane eventKey="subscription-plans">
              <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
                <p className="text-muted mb-0">Manage the subscription tiers customers can be assigned to, with trial length and resource limits</p>
                <Button variant="primary" onClick={handleCreateSubscriptionPlan}>
                  + Add Plan
                </Button>
              </div>

              {loading ? (
                <div className="d-flex justify-content-center">
                  <Spinner animation="border" role="status" />
                </div>
              ) : (
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">Subscription Plans</h5>
                  </Card.Header>
                  <Card.Body>
                    {subscriptionPlans.length === 0
                      ? <p className="text-center text-muted mb-0">No subscription plans found. Create your first plan to get started.</p>
                      : <SubscriptionPlansOverview plans={subscriptionPlans} featureCatalog={planFeatureCatalog} onEdit={handleEditSubscriptionPlan} onDelete={handleDeleteSubscriptionPlan} />}
                  </Card.Body>
                </Card>
              )}
              <PartnerPriceListTermsEditor />
            </Tab.Pane>

            {/* Partners Tab */}
            <Tab.Pane eventKey="partners">
              <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
                <p className="text-muted mb-0">Account Managers who sell to and support customer organizations for a commission</p>
                <Button variant="primary" onClick={handleCreatePartner}>
                  + Add Account Manager
                </Button>
              </div>

              {loading ? (
                <div className="d-flex justify-content-center">
                  <Spinner animation="border" role="status" />
                </div>
              ) : (
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">Account Managers</h5>
                  </Card.Header>
                  <Card.Body>
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Contact Person</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>Status</th>
                          <th>Login</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {partners.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="text-center text-muted">
                              No Account Managers yet. Add your first one to get started.
                            </td>
                          </tr>
                        ) : (
                          partners.map((partner) => (
                            <tr key={partner.id}>
                              <td><strong>{partner.name}</strong></td>
                              <td>{partner.contactPerson || '-'}</td>
                              <td>{partner.email || '-'}</td>
                              <td>{partner.phone || '-'}</td>
                              <td>
                                <Badge bg={partner.isActive ? 'success' : 'secondary'}>
                                  {partner.isActive ? 'Active' : 'Inactive'}
                                </Badge>
                              </td>
                              <td>
                                {partner.user ? (
                                  <Badge bg="info">Has login</Badge>
                                ) : (
                                  <span className="text-muted small">Not yet - assign to an org to create one</span>
                                )}
                              </td>
                              <td>
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  className="me-2"
                                  onClick={() => handleEditPartner(partner)}
                                  title="Edit"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                                    <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
                                  </svg>
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => handleDeletePartner(partner.id)}
                                  title="Delete"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                    <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                  </svg>
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}
            </Tab.Pane>

            {/* Partner Commissions Tab */}
            <Tab.Pane eventKey="media">
              {activeTab === 'media' && <div className="mt-3"><MediaStorageAdmin /></div>}
            </Tab.Pane>

            <Tab.Pane eventKey="partner-applications">
              {activeTab === 'partner-applications' && <PartnerApplications />}
            </Tab.Pane>

            <Tab.Pane eventKey="affiliates">
              {activeTab === 'affiliates' && <Affiliates />}
            </Tab.Pane>

            <Tab.Pane eventKey="partner-commissions">
              <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
                <p className="text-muted mb-0">Commission accrued per payment - actual payout to the Account Manager happens outside the system</p>
                <div className="d-flex gap-2">
                  <Form.Select
                    style={{ width: 'auto' }}
                    value={commissionPartnerFilter}
                    onChange={(e) => setCommissionPartnerFilter(e.target.value)}
                  >
                    <option value="ALL">All Account Managers</option>
                    {partners.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </Form.Select>
                  <Form.Select
                    style={{ width: 'auto' }}
                    value={commissionStatusFilter}
                    onChange={(e) => setCommissionStatusFilter(e.target.value)}
                  >
                    <option value="ALL">All statuses</option>
                    <option value="ACCRUED">Accrued</option>
                    <option value="PAID">Paid</option>
                  </Form.Select>
                  <Button variant="outline-secondary" onClick={fetchPartnerCommissions}>
                    Refresh
                  </Button>
                </div>
              </div>

              {loading ? (
                <div className="d-flex justify-content-center">
                  <Spinner animation="border" role="status" />
                </div>
              ) : (
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">Commission Ledger</h5>
                  </Card.Header>
                  <Card.Body>
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Account Manager</th>
                          <th>Organization</th>
                          <th>Payment Amount</th>
                          <th>Rate</th>
                          <th>Tier</th>
                          <th>Commission</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {partnerCommissions
                          .filter((c) => commissionPartnerFilter === 'ALL' || String(c.partner?.id) === commissionPartnerFilter)
                          .filter((c) => commissionStatusFilter === 'ALL' || c.payoutStatus === commissionStatusFilter)
                          .length === 0 ? (
                          <tr>
                            <td colSpan="9" className="text-center text-muted">
                              No commissions recorded yet.
                            </td>
                          </tr>
                        ) : (
                          partnerCommissions
                            .filter((c) => commissionPartnerFilter === 'ALL' || String(c.partner?.id) === commissionPartnerFilter)
                            .filter((c) => commissionStatusFilter === 'ALL' || c.payoutStatus === commissionStatusFilter)
                            .map((c) => (
                              <tr key={c.id}>
                                <td className="text-nowrap">{c.createdAt ? formatDateTime(c.createdAt) : ''}</td>
                                <td>{c.partner?.name}{c.partner?.kind === 'AFFILIATE' && <Badge bg="info" className="ms-1">Affiliate</Badge>}</td>
                                <td>{c.organization?.name}</td>
                                <td className={c.reversal ? 'text-danger' : undefined}>{c.currency} {c.paymentAmount}</td>
                                <td>{c.ratePercent}%</td>
                                <td>
                                  <Badge bg={c.tier === 'INITIAL' ? 'info' : 'secondary'}>{c.tier}</Badge>
                                  {c.capApplied && <Badge bg="warning" text="dark" className="ms-1">Capped</Badge>}
                                </td>
                                <td className={c.reversal ? 'text-danger' : undefined}>
                                  {c.currency} {c.commissionAmount}
                                  {c.reversal && <Badge bg="danger" className="ms-1" data-testid="commission-reversal">Refund</Badge>}
                                </td>
                                <td>
                                  <Badge bg={c.payoutStatus === 'PAID' ? 'success' : 'secondary'}>
                                    {c.reversal && c.payoutStatus === 'PAID' ? 'SETTLED' : c.payoutStatus}
                                  </Badge>
                                  {c.clawback && c.payoutStatus !== 'PAID' && <Badge bg="warning" text="dark" className="ms-1" title="This commission had already been paid out: take it off the next payout">Owed back</Badge>}
                                </td>
                                <td className="text-nowrap">
                                  {c.payoutStatus !== 'PAID' && (!c.reversal || c.clawback) && (
                                    <Button size="sm" variant="outline-success" onClick={() => handleMarkCommissionPaid(c.id)}>
                                      {c.clawback ? 'Mark settled' : 'Mark Paid'}
                                    </Button>
                                  )}
                                  {!c.reversal && c.payment?.id && (
                                    <Button size="sm" variant="outline-danger" className="ms-1" onClick={() => openRefund(c)} data-testid="record-refund">
                                      Record refund
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            ))
                        )}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}
            </Tab.Pane>

            <Modal show={Boolean(refundFor)} onHide={() => setRefundFor(null)} centered>
              <Form onSubmit={saveRefund}>
                <Modal.Header closeButton><Modal.Title>Record a refund</Modal.Title></Modal.Header>
                <Modal.Body>
                  {!refundInfo ? <div className="text-center py-3"><Spinner animation="border" size="sm" /></div> : (
                    <>
                      <p className="small text-muted">
                        {refundFor?.organization?.name}: the payment was {refundInfo.currency} {refundInfo.amount}; {refundInfo.currency} {refundInfo.refunded} has been refunded so far.
                        A refund Razorpay processes is recorded by itself; use this for one paid another way. The commission this payment earned is taken back in proportion,
                        and if it was already paid out it shows as owed back.
                      </p>
                      <Form.Group className="mb-3">
                        <Form.Label>Amount refunded ({refundInfo.currency})</Form.Label>
                        <Form.Control type="number" min="0.01" max={refundInfo.refundable} step="0.01" value={refundForm.amount} required data-testid="refund-amount"
                          onChange={(e) => setRefundForm({ ...refundForm, amount: e.target.value })} />
                        <Form.Text className="text-muted">Up to {refundInfo.currency} {refundInfo.refundable}.</Form.Text>
                      </Form.Group>
                      <Form.Group>
                        <Form.Label>Reason (optional)</Form.Label>
                        <Form.Control value={refundForm.reason} maxLength={300} onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })} />
                      </Form.Group>
                    </>
                  )}
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="secondary" onClick={() => setRefundFor(null)}>Cancel</Button>
                  <Button type="submit" variant="danger" disabled={!refundInfo || refundBusy} data-testid="save-refund">Record refund</Button>
                </Modal.Footer>
              </Form>
            </Modal>

            {/* Subscription Plan Requests Tab */}
            <Tab.Pane eventKey="subscription-plan-requests">
              <div className="mt-3">
                <p className="text-muted mb-3">Org Admin self-service requests to subscribe to a plan</p>
              </div>
              {loading ? (
                <div className="d-flex justify-content-center">
                  <Spinner animation="border" role="status" />
                </div>
              ) : (
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">Plan Requests</h5>
                  </Card.Header>
                  <Card.Body>
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>Organization</th>
                          <th>Requested Plan</th>
                          <th>Billing Cycle</th>
                          <th>Requested By</th>
                          <th>Requested At</th>
                          <th>Status</th>
                          <th>Actions / Response</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subscriptionPlanRequests.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="text-center text-muted">
                              No subscription plan requests yet.
                            </td>
                          </tr>
                        ) : (
                          subscriptionPlanRequests.map((req) => (
                            <tr key={req.id}>
                              <td>{req.organization?.name}</td>
                              <td>{req.requestedPlan?.name}</td>
                              <td>{req.billingCycle === 'YEARLY' ? 'Yearly' : 'Monthly'}</td>
                              <td>{req.requestedByName || req.requestedByEmail}</td>
                              <td>{req.requestedAt ? formatDateTime(req.requestedAt) : ''}</td>
                              <td>
                                <Badge bg={req.status === 'PENDING' ? 'warning' : req.status === 'APPROVED' ? 'success' : 'danger'} text={req.status === 'PENDING' ? 'dark' : undefined}>
                                  {req.status}
                                </Badge>
                              </td>
                              <td>
                                {req.status === 'PENDING' ? (
                                  <>
                                    <Button size="sm" variant="success" className="me-2" onClick={() => handleApproveRequest(req.id)}>
                                      Approve
                                    </Button>
                                    <Button size="sm" variant="outline-danger" onClick={() => handleOpenRejectRequest(req.id)}>
                                      Reject
                                    </Button>
                                  </>
                                ) : (
                                  <div className="small text-muted">
                                    by {req.respondedByName || req.respondedByEmail} on {req.respondedAt ? formatDateTime(req.respondedAt) : ''}
                                    {req.adminNotes && <div>Notes: {req.adminNotes}</div>}
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}
            </Tab.Pane>

            {/* Ledger Categories Tab */}
            <Tab.Pane eventKey="ledger-categories">
              <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
                <div className="d-flex align-items-center gap-3">
                  <p className="text-muted mb-0">Manage system-wide ledger categories by type</p>
                  <Form.Select
                    style={{ width: 'auto' }}
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                  >
                    <option value="ALL">All Types</option>
                    <option value="ASSET">Assets</option>
                    <option value="LIABILITY">Liabilities</option>
                    <option value="EQUITY">Equity</option>
                    <option value="REVENUE">Revenue</option>
                    <option value="EXPENSE">Expense</option>
                  </Form.Select>
                </div>
                <Button variant="primary" onClick={handleCreateCategory}>
                  + Add Category
                </Button>
              </div>

              {loading ? (
                <div className="d-flex justify-content-center">
                  <Spinner animation="border" role="status" />
                </div>
              ) : (
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">Ledger Categories</h5>
                  </Card.Header>
                  <Card.Body>
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>Category Name</th>
                          <th>Type</th>
                          <th>Description</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ledgerCategories.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="text-center text-muted">
                              No categories found. Create your first category to get started.
                            </td>
                          </tr>
                        ) : (
                          ledgerCategories
                            .filter(cat => categoryFilter === 'ALL' || cat.type === categoryFilter)
                            .map((category) => (
                              <tr key={category.id}>
                                <td><strong>{category.categoryName}</strong></td>
                                <td>
                                  <Badge bg={getLedgerTypeBadge(category.type)}>
                                    {category.type}
                                  </Badge>
                                </td>
                                <td>{category.description || '-'}</td>
                                <td>
                                  <Badge bg={category.isActive ? 'success' : 'secondary'}>
                                    {category.isActive ? 'Active' : 'Inactive'}
                                  </Badge>
                                </td>
                                <td>
                                  <Button
                                    size="sm"
                                    variant="outline-info"
                                    className="me-2"
                                    onClick={() => handleViewPrint(category, 'CATEGORY')}
                                    title="View & Print"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                      <path d="M5 1a2 2 0 0 0-2 2v1h10V3a2 2 0 0 0-2-2H5zm6 8H5a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1z"/>
                                      <path d="M0 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-1v-2a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2H2a2 2 0 0 1-2-2V7zm2.5 1a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z"/>
                                    </svg>
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline-primary"
                                    className="me-2"
                                    onClick={() => handleEditCategory(category)}
                                    title="Edit"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                      <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                                      <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
                                    </svg>
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    onClick={() => handleDeleteCategory(category.id)}
                                    title="Delete"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                      <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                      <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                    </svg>
                                  </Button>
                                </td>
                              </tr>
                            ))
                        )}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}
            </Tab.Pane>

            {/* Email Settings Tab */}
            <Tab.Pane eventKey="email">
              <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
                <p className="text-muted mb-0">
                  Configure SMTP for password reset and other system emails
                </p>
              </div>

              {emailSettingsLoading || loading ? (
                <div className="d-flex justify-content-center">
                  <Spinner animation="border" role="status" />
                </div>
              ) : (
                <Row>
                  <Col lg={8}>
                    <Card className="mb-3">
                      <Card.Header>
                        <h5 className="mb-0">SMTP Settings</h5>
                      </Card.Header>
                      <Card.Body>
                        {emailSettings.effectiveConfigured ? (
                          <Alert variant="success" className="mb-3">
                            Mail is active via <strong>{emailSettings.activeSource}</strong>
                            {emailSettings.activeSource === 'environment' && (
                              <> (environment variables). Enable and save below to manage settings in the UI.</>
                            )}
                            .
                          </Alert>
                        ) : (
                          <Alert variant="warning" className="mb-3">
                            Mail is not configured. Password reset links will be logged to the backend console until SMTP is enabled.
                            {emailSettings.environmentFallbackAvailable && (
                              <> Environment SMTP variables are present but database settings are disabled.</>
                            )}
                          </Alert>
                        )}

                        <Form onSubmit={handleSaveEmailSettings}>
                          <Form.Check
                            type="switch"
                            id="email-enabled"
                            className="mb-3"
                            label="Enable SMTP (use these settings)"
                            checked={emailSettings.enabled}
                            onChange={(e) =>
                              setEmailSettings({ ...emailSettings, enabled: e.target.checked })
                            }
                          />

                          <Row>
                            <Col md={8}>
                              <Form.Group className="mb-3">
                                <Form.Label>SMTP host</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={emailSettings.host}
                                  onChange={(e) =>
                                    setEmailSettings({ ...emailSettings, host: e.target.value })
                                  }
                                  placeholder="smtp.example.com"
                                  required={emailSettings.enabled}
                                />
                              </Form.Group>
                            </Col>
                            <Col md={4}>
                              <Form.Group className="mb-3">
                                <Form.Label>Port</Form.Label>
                                <Form.Control
                                  type="number"
                                  value={emailSettings.port}
                                  onChange={(e) =>
                                    setEmailSettings({ ...emailSettings, port: e.target.value })
                                  }
                                  placeholder="587"
                                />
                              </Form.Group>
                            </Col>
                          </Row>

                          <Form.Group className="mb-3">
                            <Form.Label>Username</Form.Label>
                            <Form.Control
                              type="text"
                              value={emailSettings.username}
                              onChange={(e) =>
                                setEmailSettings({ ...emailSettings, username: e.target.value })
                              }
                              placeholder="smtp user / mailbox"
                              autoComplete="off"
                            />
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label>Password</Form.Label>
                            <Form.Control
                              type="password"
                              value={emailSettings.password}
                              onChange={(e) =>
                                setEmailSettings({ ...emailSettings, password: e.target.value })
                              }
                              placeholder={
                                emailSettings.passwordConfigured
                                  ? 'Leave blank to keep current password'
                                  : 'SMTP password'
                              }
                              autoComplete="new-password"
                            />
                            {emailSettings.passwordConfigured && (
                              <Form.Text className="text-muted">
                                A password is already stored. Enter a new value only to replace it.
                              </Form.Text>
                            )}
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label>From address</Form.Label>
                            <Form.Control
                              type="email"
                              value={emailSettings.fromAddress}
                              onChange={(e) =>
                                setEmailSettings({ ...emailSettings, fromAddress: e.target.value })
                              }
                              placeholder="noreply@example.com"
                              required={emailSettings.enabled}
                            />
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label>From name</Form.Label>
                            <Form.Control
                              type="text"
                              value={emailSettings.fromName}
                              onChange={(e) =>
                                setEmailSettings({ ...emailSettings, fromName: e.target.value })
                              }
                              placeholder="iBigDo Care"
                            />
                            <Form.Text className="text-muted">
                              Shown as the sender's display name - without this, most mail clients just show the part of the address before the @.
                            </Form.Text>
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label>Frontend base URL</Form.Label>
                            <WebAddressInput
                              value={emailSettings.frontendBaseUrl}
                              onChange={(v) =>
                                setEmailSettings({
                                  ...emailSettings,
                                  frontendBaseUrl: v
                                })
                              }
                              placeholder="http://localhost:3000"
                            />
                            <Form.Text className="text-muted">
                              Used in password-reset links (no trailing slash needed).
                            </Form.Text>
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label>Feedback notify email</Form.Label>
                            <Form.Control
                              type="email"
                              value={emailSettings.feedbackNotifyEmail}
                              onChange={(e) =>
                                setEmailSettings({
                                  ...emailSettings,
                                  feedbackNotifyEmail: e.target.value
                                })
                              }
                              placeholder="product@example.com"
                            />
                            <Form.Text className="text-muted">
                              Optional. New feedback notifications go here; defaults to From address.
                            </Form.Text>
                          </Form.Group>

                          <Form.Check
                            type="switch"
                            id="email-use-tls"
                            className="mb-3"
                            label="Use STARTTLS"
                            checked={emailSettings.useTls}
                            onChange={(e) =>
                              setEmailSettings({ ...emailSettings, useTls: e.target.checked })
                            }
                          />

                          <Button type="submit" variant="primary" disabled={emailSettingsSaving}>
                            {emailSettingsSaving ? 'Saving...' : 'Save email settings'}
                          </Button>
                        </Form>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col lg={4}>
                    <Card>
                      <Card.Header>
                        <h5 className="mb-0">Send test email</h5>
                      </Card.Header>
                      <Card.Body>
                        <p className="text-muted small">
                          Uses the currently active SMTP configuration (database or environment).
                        </p>
                        <Form.Group className="mb-3">
                          <Form.Label>Recipient</Form.Label>
                          <Form.Control
                            type="email"
                            value={testEmailTo}
                            onChange={(e) => setTestEmailTo(e.target.value)}
                            placeholder="you@example.com"
                          />
                        </Form.Group>
                        <Button
                          variant="outline-primary"
                          onClick={handleSendTestEmail}
                          disabled={testEmailSending || !emailSettings.effectiveConfigured}
                        >
                          {testEmailSending ? 'Sending...' : 'Send test'}
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}
            </Tab.Pane>

            {/* Feedback Inbox Tab */}
            <Tab.Pane eventKey="feedback">
              <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
                <div className="d-flex align-items-center gap-3">
                  <p className="text-muted mb-0">Review product feedback from all users</p>
                  <Form.Select
                    style={{ width: 'auto' }}
                    value={feedbackFilter}
                    onChange={(e) => {
                      setFeedbackFilter(e.target.value);
                    }}
                  >
                    <option value="ALL">All statuses</option>
                    <option value="NEW">New</option>
                    <option value="IN_PROGRESS">In Progress</option>
                    <option value="RESOLVED">Resolved</option>
                    <option value="REOPEN">Reopen</option>
                    <option value="CLOSED">Closed</option>
                  </Form.Select>
                </div>
                <Button variant="outline-secondary" onClick={fetchFeedback}>
                  Refresh
                </Button>
              </div>

              {loading ? (
                <div className="d-flex justify-content-center">
                  <Spinner animation="border" role="status" />
                </div>
              ) : (
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">Feedback inbox</h5>
                  </Card.Header>
                  <Card.Body>
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Category</th>
                          <th>From</th>
                          <th>Organization</th>
                          <th>Message</th>
                          <th>Reply</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {feedbackItems.length === 0 ? (
                          <tr>
                            <td colSpan="8" className="text-center text-muted">
                              No feedback yet.
                            </td>
                          </tr>
                        ) : (
                          feedbackItems
                            .filter((item) =>
                              feedbackFilter === 'ALL' ? true : item.status === feedbackFilter
                            )
                            .map((item) => (
                              <tr key={item.id}>
                                <td className="text-nowrap">
                                  {item.createdAt
                                    ? formatDateTime(item.createdAt)
                                    : '-'}
                                </td>
                                <td>
                                  <Badge bg={getFeedbackCategoryBadge(item.category)}>
                                    {item.category}
                                  </Badge>
                                </td>
                                <td>
                                  <div>
                                    {item.isGuest ? item.guestName : item.userName || '-'}
                                    {item.isGuest && (
                                      <Badge bg="light" text="dark" className="border ms-1">Guest</Badge>
                                    )}
                                  </div>
                                  <small className="text-muted">
                                    {item.isGuest ? item.guestEmail : item.userEmail}
                                  </small>
                                </td>
                                <td>{item.isGuest ? '-' : item.organizationName || '-'}</td>
                                <td style={{ maxWidth: 220 }}>
                                  {item.message && item.message.length > 100
                                    ? `${item.message.slice(0, 100)}…`
                                    : item.message}
                                </td>
                                <td>
                                  {item.hasResponse || item.response ? (
                                    <Badge bg="success">Replied</Badge>
                                  ) : (
                                    <Badge bg="secondary">Pending</Badge>
                                  )}
                                </td>
                                <td>
                                  <Badge bg={getFeedbackStatusBadge(item.status)}>
                                    {formatFeedbackStatus(item.status)}
                                  </Badge>
                                </td>
                                <td>
                                  <Button
                                    size="sm"
                                    variant="outline-primary"
                                    onClick={() => handleOpenFeedback(item)}
                                  >
                                    View / Respond
                                  </Button>
                                </td>
                              </tr>
                            ))
                        )}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}
            </Tab.Pane>

            {/* What account managers asked the help assistant */}
            <Tab.Pane eventKey="assistant-questions">
              <AssistantQuestionsAdmin />
            </Tab.Pane>

            <Tab.Pane eventKey="shop-directory">
              <ShopDirectoryAdmin />
            </Tab.Pane>

            {/* A customer's Ref from an error message: that request's log lines */}
            <Tab.Pane eventKey="find-by-ref">
              <FindByRef />
            </Tab.Pane>

            {/* Announcements Tab */}
            <Tab.Pane eventKey="announcements">
              <AnnouncementsAdmin />
            </Tab.Pane>

            {/* Broadcast Email Tab */}
            <Tab.Pane eventKey="email-broadcast">
              <EmailBroadcastAdmin />
            </Tab.Pane>
            </Tab.Content>
          </div>
        </div>
        </Tab.Container>

      {/* Feedback Review Modal */}
      <Modal show={showFeedbackModal} onHide={() => setShowFeedbackModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>View / respond to feedback #{selectedFeedback?.id}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveFeedback}>
          <Modal.Body>
            {selectedFeedback && (
              <>
                <Row className="mb-3">
                  <Col md={6}>
                    <div className="text-muted small">From</div>
                    <div>
                      {selectedFeedback.isGuest ? (
                        <>
                          {selectedFeedback.guestName} ({selectedFeedback.guestEmail})
                          <Badge bg="light" text="dark" className="border ms-2">Guest / Contact Us</Badge>
                        </>
                      ) : (
                        <>{selectedFeedback.userName} ({selectedFeedback.userEmail})</>
                      )}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="text-muted small">Organization</div>
                    <div>{selectedFeedback.isGuest ? '-' : selectedFeedback.organizationName || '-'}</div>
                  </Col>
                </Row>
                <div className="mb-3">
                  <Badge bg={getFeedbackCategoryBadge(selectedFeedback.category)} className="me-2">
                    {selectedFeedback.category}
                  </Badge>
                  <Badge bg={getFeedbackStatusBadge(selectedFeedback.status)} className="me-2">
                    {formatFeedbackStatus(selectedFeedback.status)}
                  </Badge>
                  <small className="text-muted">
                    Opened{' '}
                    {selectedFeedback.createdAt
                      ? formatDateTime(selectedFeedback.createdAt)
                      : ''}
                  </small>
                </div>

                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={feedbackForm.status}
                    onChange={(e) =>
                      setFeedbackForm({ ...feedbackForm, status: e.target.value })
                    }
                    disabled={selectedFeedback.status === 'CLOSED'}
                  >
                    <option value={selectedFeedback.status}>
                      {formatFeedbackStatus(selectedFeedback.status)} (current)
                    </option>
                    {(selectedFeedback.allowedNextStatuses || [])
                      .filter((s) => s !== selectedFeedback.status)
                      .map((s) => (
                        <option key={s} value={s}>
                          {formatFeedbackStatus(s)}
                        </option>
                      ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Flow: New → In Progress → Resolved → Closed. Close a resolved ticket after you have checked the fix (the person who sent it can also accept it, which closes it, or reject it, which reopens it).
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Add reply to user</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={feedbackForm.response}
                    onChange={(e) =>
                      setFeedbackForm({ ...feedbackForm, response: e.target.value })
                    }
                    maxLength={5000}
                    placeholder="New message appended to the conversation (leave blank to only update status)"
                    disabled={selectedFeedback.status === 'CLOSED'}
                  />
                  <Form.Text className="text-muted">
                    {feedbackForm.response.length}/5000 · Does not overwrite previous replies
                  </Form.Text>
                </Form.Group>

                <Form.Check
                  type="checkbox"
                  id="notify-user-feedback"
                  className="mb-3"
                  label="Email the user when a new reply is added (requires SMTP)"
                  checked={feedbackForm.notifyUser}
                  onChange={(e) =>
                    setFeedbackForm({ ...feedbackForm, notifyUser: e.target.checked })
                  }
                  disabled={selectedFeedback.status === 'CLOSED'}
                />

                <Form.Group className="mb-3">
                  <Form.Label>Add internal note</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={feedbackForm.adminNotes}
                    onChange={(e) =>
                      setFeedbackForm({ ...feedbackForm, adminNotes: e.target.value })
                    }
                    placeholder="Internal only — appended to the thread for admins"
                    disabled={selectedFeedback.status === 'CLOSED'}
                  />
                </Form.Group>

                <div className="border-top pt-3 mt-2">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong>Conversation</strong>
                    <small className="text-muted">Oldest first</small>
                  </div>
                  <div
                    style={{
                      maxHeight: 320,
                      overflowY: 'auto',
                      background: 'var(--bs-tertiary-bg, #f8f9fa)',
                      borderRadius: 6,
                      padding: 12
                    }}
                  >
                    {(selectedFeedback.messages || []).length === 0 ? (
                      <div className="text-muted small">No messages yet.</div>
                    ) : (
                      (selectedFeedback.messages || []).map((msg) => (
                        <div
                          key={msg.id}
                          className={`mb-2 p-2 rounded border ${
                            msg.internal
                              ? 'border-warning-subtle'
                              : msg.authorType === 'ADMIN'
                                ? 'border-primary-subtle'
                                : 'border-secondary-subtle'
                          }`}
                          style={{
                            background: msg.internal
                              ? 'rgba(255, 193, 7, 0.12)'
                              : msg.authorType === 'ADMIN'
                                ? 'rgba(13, 110, 253, 0.08)'
                                : msg.authorType === 'SYSTEM'
                                  ? 'transparent'
                                  : 'var(--bs-body-bg)'
                          }}
                        >
                          <div className="d-flex justify-content-between gap-2 mb-1">
                            <small className="fw-semibold">
                              {renderFeedbackMessageAuthor(msg)}
                              {msg.internal && (
                                <Badge bg="warning" text="dark" className="ms-2">
                                  Internal
                                </Badge>
                              )}
                            </small>
                            <small className="text-muted text-nowrap">
                              {msg.createdAt ? formatDateTime(msg.createdAt) : ''}
                            </small>
                          </div>
                          <div style={{ whiteSpace: 'pre-wrap' }}>{msg.body}</div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowFeedbackModal(false)}>
              Close
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={
                feedbackSaving
                || selectedFeedback?.status === 'CLOSED'
                || (!feedbackForm.response.trim() && !feedbackForm.adminNotes.trim())
              }
            >
              {feedbackSaving ? 'Saving...' : 'Save'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Promote User Modal */}
      <Modal show={showPromoteModal} onHide={() => setShowPromoteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Promote User to System Admin</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to promote <strong>{selectedUser?.email}</strong> to system admin?</p>
          <Alert variant="warning">
            System admins have access to all organizations and can manage the entire system.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPromoteModal(false)}>
            Cancel
          </Button>
          <Button variant="success" onClick={handlePromoteUser}>
            Promote
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Demote User Modal */}
      <Modal show={showDemoteModal} onHide={() => setShowDemoteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Demote User from System Admin</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to demote <strong>{selectedUser?.email}</strong> from system admin?</p>
          <Alert variant="info">
            The user will lose system-wide access but will retain their organization-level permissions.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDemoteModal(false)}>
            Cancel
          </Button>
          <Button variant="warning" onClick={handleDemoteUser}>
            Demote
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Assign Org Admin Role Modal */}
      <Modal show={showAssignRoleModal} onHide={() => setShowAssignRoleModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Assign Org Admin Role</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to assign <strong>Org Admin</strong> role to <strong>{selectedUser?.email}</strong>?</p>
          <Alert variant="info">
            Org Admin users can manage roles and permissions for their organization. They will have access to the Organization Admin module.
          </Alert>
          {selectedOrg && (
            <p className="text-muted">
              Organization: <strong>{selectedOrg.name}</strong>
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAssignRoleModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAssignOrgAdminRole}>
            Assign Org Admin
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Create/Edit User Modal */}
      <Modal show={showUserModal} onHide={handleCloseUserModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingUser ? 'Edit User' : 'Create User'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveUser}>
          <Modal.Body>
            {selectedOrg && (
              <Alert variant="light" className="mb-3">
                Organization: <strong>{selectedOrg.name}</strong>
              </Alert>
            )}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>First Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={userFormData.firstName}
                    onChange={(e) => setUserFormData({ ...userFormData, firstName: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Last Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={userFormData.lastName}
                    onChange={(e) => setUserFormData({ ...userFormData, lastName: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Email *</Form.Label>
              <Form.Control
                type="email"
                value={userFormData.email}
                onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Phone Number</Form.Label>
              <PhoneNumberInput
                name="phoneNumber"
                value={userFormData.phoneNumber}
                onChange={(e) => setUserFormData({ ...userFormData, phoneNumber: e.target.value })}
              />
            </Form.Group>
            {!editingUser && (
              <Form.Group className="mb-3">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  value={userFormData.password}
                  onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                  required={false}
                  placeholder="Required for new users only"
                  autoComplete="new-password"
                />
                {userFormData.password && <PasswordHint password={userFormData.password} />}
                <Form.Text className="text-muted">
                  Leave blank to invite an existing user by email. New accounts require a password.
                </Form.Text>
              </Form.Group>
            )}
            {editingUser && (
              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  id="user-active-switch"
                  label="Active"
                  checked={userFormData.isActive}
                  onChange={(e) => setUserFormData({ ...userFormData, isActive: e.target.checked })}
                />
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseUserModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingUser ? 'Update' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Manage Roles for User Modal */}
      <Modal show={showRoleAssignmentModal} onHide={handleCloseRoleAssignmentModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Manage Roles for{' '}
            {selectedUserForRoleAssignment &&
              `${selectedUserForRoleAssignment.firstName} ${selectedUserForRoleAssignment.lastName}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUserForRoleAssignment && (
            <>
              <div className="mb-4">
                <h6>Current Roles</h6>
                {selectedUserForRoleAssignment.roles && selectedUserForRoleAssignment.roles.length > 0 ? (
                  <div>
                    {selectedUserForRoleAssignment.roles.map((role, idx) => {
                      const roleObj = typeof role === 'object' ? role : null;
                      const roleId = roleObj?.id;
                      const roleName = typeof role === 'string' ? role : (roleObj?.name || 'Unknown');
                      return (
                        <Badge key={idx} bg="primary" className="me-2 mb-2" style={{ fontSize: '0.9rem', padding: '0.5rem' }}>
                          {getRoleLabel(roleObj || roleName)}
                          {roleId && (
                            <button
                              type="button"
                              className="btn-close btn-close-white ms-2"
                              style={{ fontSize: '0.7rem' }}
                              onClick={() => handleRemoveRoleFromUser(roleId)}
                              aria-label="Remove role"
                            ></button>
                          )}
                        </Badge>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-muted">No roles assigned</p>
                )}
              </div>
              <div>
                <h6>Available Roles</h6>
                {orgRoles.length > 0 ? (
                  <div>
                    {orgRoles
                      .filter((role) => {
                        if (!selectedUserForRoleAssignment.roles) return true;
                        return !selectedUserForRoleAssignment.roles.some((userRole) => {
                          const userRoleId = typeof userRole === 'object' && userRole.id ? userRole.id : null;
                          return userRoleId === role.id;
                        });
                      })
                      .map((role) => (
                        <div key={role.id} className="mb-2">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => handleAssignRoleToUser(role.id)}
                            className="w-100 text-start"
                          >
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <strong>{getRoleLabel(role)}</strong>
                                {role.description && (
                                  <div className="text-muted small">{role.description}</div>
                                )}
                              </div>
                              <span>+</span>
                            </div>
                          </Button>
                        </div>
                      ))}
                  </div>
                ) : (
                  <p className="text-muted">No roles available in this organization</p>
                )}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseRoleAssignmentModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Create/Edit Role Modal */}
      <Modal show={showRoleModal} onHide={() => setShowRoleModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedRole ? 'Edit Role' : 'Create Role'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveRole}>
          <Modal.Body>
            {selectedOrg && (
              <Alert variant="light" className="mb-3">
                Organization: <strong>{selectedOrg.name}</strong>
              </Alert>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Role Name *</Form.Label>
              <Form.Control
                type="text"
                value={roleFormData.name}
                onChange={(e) => setRoleFormData({ ...roleFormData, name: e.target.value })}
                required
                placeholder="e.g., SALES_MANAGER"
              />
              <Form.Text className="text-muted">
                Use uppercase with underscores (e.g., SALES_REP, INVENTORY_MANAGER)
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={roleFormData.description}
                onChange={(e) => setRoleFormData({ ...roleFormData, description: e.target.value })}
                placeholder="Describe the role's responsibilities"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Active"
                checked={roleFormData.isActive}
                onChange={(e) => setRoleFormData({ ...roleFormData, isActive: e.target.checked })}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowRoleModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {selectedRole ? 'Update' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Manage Permissions Modal */}
      <Modal show={showPermissionModal} onHide={() => setShowPermissionModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Manage Permissions - {selectedRole ? getRoleLabel(selectedRole) : ''}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {selectedRole?.isSystemRole && (
            <Alert variant="warning">
              System roles cannot have their permissions modified.
            </Alert>
          )}
          {Object.entries(permissionsGrouped).map(([resource, resourcePermissions]) => (
            <Card key={resource} className="mb-3">
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <strong>{resource}</strong>
                  {!selectedRole?.isSystemRole && (
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => toggleResourcePermissions(resource)}
                    >
                      Toggle All
                    </Button>
                  )}
                </div>
              </Card.Header>
              <Card.Body>
                <div className="row">
                  {resourcePermissions.map((permission) => (
                    <div key={permission.id} className="col-md-6 mb-2">
                      <Form.Check
                        type="checkbox"
                        id={`sa-permission-${permission.id}`}
                        label={`${permission.action} - ${permission.description || permission.name}`}
                        checked={rolePermissions.includes(permission.id)}
                        onChange={() => togglePermission(permission.id)}
                        disabled={selectedRole?.isSystemRole}
                      />
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPermissionModal(false)}>
            Close
          </Button>
          {!selectedRole?.isSystemRole && (
            <Button variant="primary" onClick={handleSavePermissions}>
              Save Permissions
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Category Modal */}
      <Modal show={showCategoryModal} onHide={() => setShowCategoryModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedCategory ? 'Edit Category' : 'Create Category'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveCategory}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Category Name *</Form.Label>
              <Form.Control
                type="text"
                value={categoryFormData.categoryName}
                onChange={(e) => setCategoryFormData({ ...categoryFormData, categoryName: e.target.value })}
                placeholder="e.g., Current Asset, Fixed Asset, Accounts Payable"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Ledger Type *</Form.Label>
              <Form.Select
                value={categoryFormData.type}
                onChange={(e) => setCategoryFormData({ ...categoryFormData, type: e.target.value })}
                required
              >
                <option value="">Select Type</option>
                <option value="ASSET">Asset</option>
                <option value="LIABILITY">Liability</option>
                <option value="EQUITY">Equity</option>
                <option value="REVENUE">Revenue</option>
                <option value="EXPENSE">Expense</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Select the ledger type this category belongs to
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={categoryFormData.description}
                onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
                placeholder="Optional description for this category"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Active"
                checked={categoryFormData.isActive}
                onChange={(e) => setCategoryFormData({ ...categoryFormData, isActive: e.target.checked })}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCategoryModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {selectedCategory ? 'Update' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Goods Movement Type Modal */}
      <Modal show={showMovementTypeModal} onHide={() => setShowMovementTypeModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedMovementType ? 'Edit Goods Movement Type' : 'Create Goods Movement Type'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveMovementType}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Code *</Form.Label>
              <Form.Control
                type="text"
                value={movementTypeFormData.code}
                onChange={(e) => setMovementTypeFormData({ ...movementTypeFormData, code: e.target.value })}
                placeholder="e.g., RCV_VENDOR, RET_VENDOR"
                required
              />
              <Form.Text className="text-muted">
                Unique short identifier. Stored in uppercase.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                type="text"
                value={movementTypeFormData.name}
                onChange={(e) => setMovementTypeFormData({ ...movementTypeFormData, name: e.target.value })}
                placeholder="e.g., Receive from Vendor, Return to Vendor"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Type (Direction) *</Form.Label>
              <Form.Select
                value={movementTypeFormData.direction}
                onChange={(e) => {
                  const newDirection = e.target.value;
                  // Procurement Survey is INBOUND-only; clear it when switching to OUTBOUND
                  setMovementTypeFormData({
                    ...movementTypeFormData,
                    direction: newDirection,
                    referenceType:
                      newDirection === 'OUTBOUND' && movementTypeFormData.referenceType === 'PROCUREMENT_SURVEY'
                        ? ''
                        : movementTypeFormData.referenceType
                  });
                }}
                required
              >
                <option value="">Select Type</option>
                <option value="INBOUND">Inbound</option>
                <option value="OUTBOUND">Outbound</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Inbound: stock increases (e.g., receive). Outbound: stock decreases (e.g., return).
              </Form.Text>
            </Form.Group>

            {(movementTypeFormData.direction === 'INBOUND' || movementTypeFormData.direction === 'OUTBOUND') && (
              <Form.Group className="mb-3">
                <Form.Label>Reference Type</Form.Label>
                <Form.Select
                  value={movementTypeFormData.referenceType}
                  onChange={(e) => setMovementTypeFormData({ ...movementTypeFormData, referenceType: e.target.value })}
                >
                  <option value="">Select Reference Type (Optional)</option>
                  <option value="PURCHASE_ORDER">Purchase Order</option>
                  {movementTypeFormData.direction === 'INBOUND' && (
                    <option value="PROCUREMENT_SURVEY">Procurement Survey</option>
                  )}
                  <option value="WORK_ORDER">Work Order</option>
                  <option value="SALES_ORDER">Sales Order</option>
                  {movementTypeFormData.direction === 'OUTBOUND' && (
                    <option value="SALES_BILL">Sale Bill</option>
                  )}
                  <option value="STOCK_TRANSFER_ORDER">Stock Transfer Order</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  {movementTypeFormData.direction === 'INBOUND'
                    ? 'For INBOUND: Purchase Order, Procurement Survey (field slip), Work Order, Sales Order (sale return), or Stock Transfer Order. Defaults to Purchase Order if not specified.'
                    : 'For OUTBOUND: Sales Order, Sale Bill (issues stock when a bill is posted from Sales), Work Order, Purchase Order (return to vendor), or Stock Transfer Order. Defaults to Sales Order if not specified.'}
                </Form.Text>
              </Form.Group>
            )}

            {(movementTypeFormData.direction === 'INBOUND' || movementTypeFormData.direction === 'OUTBOUND') && (
              <Form.Group className="mb-3">
                <Form.Label>Location Type</Form.Label>
                <Form.Select
                  value={movementTypeFormData.locationType}
                  onChange={(e) => setMovementTypeFormData({ ...movementTypeFormData, locationType: e.target.value })}
                >
                  <option value="">Any (unrestricted)</option>
                  <option value="WAREHOUSE">Warehouse</option>
                  <option value="RETAIL_STORE">Retail Store</option>
                  <option value="PRODUCTION_UNIT">Production Unit</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  {movementTypeFormData.direction === 'INBOUND'
                    ? 'Restricts the GRN’s Receiving Location to this type when this movement type is selected (e.g. "Receive at Retail Store" should be Retail Store). Leave as Any if the receiving location can vary.'
                    : 'Restricts the GIN’s Issuing Location to this type when this movement type is selected. Leave as Any if the issuing location can vary (most movement types describe a destination or action, not the source, so Any is usually correct here).'}
                </Form.Text>
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={movementTypeFormData.description}
                onChange={(e) => setMovementTypeFormData({ ...movementTypeFormData, description: e.target.value })}
                placeholder="Optional description"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Active"
                checked={movementTypeFormData.isActive}
                onChange={(e) => setMovementTypeFormData({ ...movementTypeFormData, isActive: e.target.checked })}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowMovementTypeModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {selectedMovementType ? 'Update' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Tax Rate Modal */}
      <Modal show={showTaxRateModal} onHide={() => setShowTaxRateModal(false)}>
        <Form onSubmit={handleSaveTaxRate}>
          <Modal.Header closeButton>
            <Modal.Title>{selectedTaxRate ? 'Edit Tax Rate' : 'Create Tax Rate'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                type="text"
                value={taxRateFormData.name}
                onChange={(e) => setTaxRateFormData({ ...taxRateFormData, name: e.target.value })}
                placeholder="e.g. GST 18%"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Rate % *</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                value={taxRateFormData.ratePercent}
                onChange={(e) => setTaxRateFormData({ ...taxRateFormData, ratePercent: e.target.value })}
                required
              />
              <Form.Text className="text-muted">
                Applied as CGST+SGST (split in half) for intra-state Sale Bills, or IGST (full rate) for inter-state ones.
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={taxRateFormData.description}
                onChange={(e) => setTaxRateFormData({ ...taxRateFormData, description: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Active"
                checked={taxRateFormData.isActive}
                onChange={(e) => setTaxRateFormData({ ...taxRateFormData, isActive: e.target.checked })}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowTaxRateModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {selectedTaxRate ? 'Update' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Subscription Plan Modal */}
      <Modal show={showSubscriptionPlanModal} onHide={() => setShowSubscriptionPlanModal(false)}>
        <Form onSubmit={handleSaveSubscriptionPlan}>
          <Modal.Header closeButton>
            <Modal.Title>{selectedSubscriptionPlan ? 'Edit Subscription Plan' : 'Create Subscription Plan'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                type="text"
                value={subscriptionPlanFormData.name}
                onChange={(e) => setSubscriptionPlanFormData({ ...subscriptionPlanFormData, name: e.target.value })}
                placeholder="e.g. Standard"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={subscriptionPlanFormData.description}
                onChange={(e) => setSubscriptionPlanFormData({ ...subscriptionPlanFormData, description: e.target.value })}
              />
            </Form.Group>
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Price / Month *</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    value={subscriptionPlanFormData.priceMonthly}
                    onChange={(e) => setSubscriptionPlanFormData({ ...subscriptionPlanFormData, priceMonthly: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Price / Year *</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    value={subscriptionPlanFormData.priceYearly}
                    onChange={(e) => setSubscriptionPlanFormData({ ...subscriptionPlanFormData, priceYearly: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Trial Days *</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={subscriptionPlanFormData.trialDays}
                onChange={(e) => setSubscriptionPlanFormData({ ...subscriptionPlanFormData, trialDays: e.target.value })}
                required
              />
            </Form.Group>
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Max Warehouses</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    placeholder="Unlimited"
                    value={subscriptionPlanFormData.maxWarehouses}
                    onChange={(e) => setSubscriptionPlanFormData({ ...subscriptionPlanFormData, maxWarehouses: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Max Production Units</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    placeholder="Unlimited"
                    value={subscriptionPlanFormData.maxProductionUnits}
                    onChange={(e) => setSubscriptionPlanFormData({ ...subscriptionPlanFormData, maxProductionUnits: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Max Retail Stores</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    placeholder="Unlimited"
                    value={subscriptionPlanFormData.maxRetailStores}
                    onChange={(e) => setSubscriptionPlanFormData({ ...subscriptionPlanFormData, maxRetailStores: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Max Daily Transactions</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    placeholder="Unlimited"
                    value={subscriptionPlanFormData.maxDailyTransactions}
                    onChange={(e) => setSubscriptionPlanFormData({ ...subscriptionPlanFormData, maxDailyTransactions: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Max Users</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    placeholder="Unlimited"
                    value={subscriptionPlanFormData.maxUsers}
                    onChange={(e) => setSubscriptionPlanFormData({ ...subscriptionPlanFormData, maxUsers: e.target.value })}
                  />
                  <Form.Text className="text-muted">
                    Maximum number of user licenses (active organization members) this plan allows.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Documents per month</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    placeholder="No monthly allowance"
                    value={subscriptionPlanFormData.maxDocumentsPerMonth}
                    onChange={(e) => setSubscriptionPlanFormData({ ...subscriptionPlanFormData, maxDocumentsPerMonth: e.target.value })}
                  />
                  <Form.Text className="text-muted">
                    Sale bills and orders, purchase orders, vendor GRNs, work orders and stock transfers created in a calendar month.
                    Warns at 80%, allows a 10% grace, then pauses new documents. Blank uses the daily cap above instead.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Form.Text className="text-muted d-block mb-3">Leave a limit blank for unlimited.</Form.Text>
            <h6 className="mt-3">For account managers (Plans &amp; Pricing)</h6>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="plan-visible-to-partners"
                label="Show this plan to account managers"
                checked={subscriptionPlanFormData.visibleToPartners}
                onChange={(e) => setSubscriptionPlanFormData({ ...subscriptionPlanFormData, visibleToPartners: e.target.checked })}
              />
              <Form.Text className="text-muted">Leave it off while you prepare a plan, and for the trial and legacy plans.</Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Who it is for</Form.Label>
              <Form.Control
                type="text" maxLength={200} placeholder="A trader on one site"
                value={subscriptionPlanFormData.audience}
                onChange={(e) => setSubscriptionPlanFormData({ ...subscriptionPlanFormData, audience: e.target.value })}
              />
            </Form.Group>
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Onboarding fee</Form.Label>
                  <Form.Control
                    type="number" min="0" placeholder="None"
                    value={subscriptionPlanFormData.onboardingFee}
                    onChange={(e) => setSubscriptionPlanFormData({ ...subscriptionPlanFormData, onboardingFee: e.target.value })}
                  />
                  <Form.Check
                    className="mt-1"
                    type="checkbox"
                    id="plan-onboarding-required"
                    label="Onboarding is required"
                    checked={subscriptionPlanFormData.onboardingRequired}
                    onChange={(e) => setSubscriptionPlanFormData({ ...subscriptionPlanFormData, onboardingRequired: e.target.checked })}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Account manager first response (business days)</Form.Label>
                  <Form.Control
                    type="number" min="0" placeholder="Not stated"
                    value={subscriptionPlanFormData.accountManagerResponseDays}
                    onChange={(e) => setSubscriptionPlanFormData({ ...subscriptionPlanFormData, accountManagerResponseDays: e.target.value })}
                  />
                  <Form.Text className="text-muted">What the customer is told their account manager will do.</Form.Text>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>iBigDo backup response (business days)</Form.Label>
                  <Form.Control
                    type="number" min="0" placeholder="Not stated"
                    value={subscriptionPlanFormData.supportResponseDays}
                    onChange={(e) => setSubscriptionPlanFormData({ ...subscriptionPlanFormData, supportResponseDays: e.target.value })}
                  />
                  <Form.Text className="text-muted">Within how many business days ibigdo replies if the account manager has not (the backstop).</Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Features included in this plan</Form.Label>
              <div className="border rounded p-2" style={{ columns: 2 }}>
                {planFeatureCatalog.map((f) => (
                  <Form.Check
                    key={f.key}
                    type="checkbox"
                    id={`plan-feature-${f.key}`}
                    label={f.label}
                    checked={subscriptionPlanFormData.features.includes(f.key)}
                    onChange={(e) => setSubscriptionPlanFormData({
                      ...subscriptionPlanFormData,
                      features: e.target.checked
                        ? [...subscriptionPlanFormData.features, f.key]
                        : subscriptionPlanFormData.features.filter((k) => k !== f.key)
                    })}
                  />
                ))}
              </div>
              <Form.Text className="text-muted">
                Purchasing, stock, sales, GST invoicing, accounts, Tally export and the standard roles are in every plan.
                A monthly price of 0 with a yearly price means the plan is sold yearly only.
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Default plan (auto-assigned to every newly registered organization)"
                checked={subscriptionPlanFormData.isDefaultPlan}
                onChange={(e) => setSubscriptionPlanFormData({ ...subscriptionPlanFormData, isDefaultPlan: e.target.checked })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Active"
                checked={subscriptionPlanFormData.isActive}
                onChange={(e) => setSubscriptionPlanFormData({ ...subscriptionPlanFormData, isActive: e.target.checked })}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowSubscriptionPlanModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {selectedSubscriptionPlan ? 'Update' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Partner Modal */}
      <PartnerAssignmentTermsModal
        assignment={editingAssignment}
        onHide={() => setEditingAssignment(null)}
        onSaved={() => selectedOrg && fetchOrgDetails(selectedOrg.id)}
      />

      <Modal show={showPartnerModal} onHide={() => setShowPartnerModal(false)}>
        <Form onSubmit={handleSavePartner}>
          <Modal.Header closeButton>
            <Modal.Title>{selectedPartner ? 'Edit Account Manager' : 'Add Account Manager'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                type="text"
                value={partnerFormData.name}
                onChange={(e) => setPartnerFormData({ ...partnerFormData, name: e.target.value })}
                placeholder="e.g. Acme Resellers"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Contact Person</Form.Label>
              <Form.Control
                type="text"
                value={partnerFormData.contactPerson}
                onChange={(e) => setPartnerFormData({ ...partnerFormData, contactPerson: e.target.value })}
              />
            </Form.Group>
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={partnerFormData.email}
                    onChange={(e) => setPartnerFormData({ ...partnerFormData, email: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Phone</Form.Label>
                  <PhoneNumberInput
                    name="phone"
                    value={partnerFormData.phone}
                    onChange={(e) => setPartnerFormData({ ...partnerFormData, phone: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={partnerFormData.notes}
                onChange={(e) => setPartnerFormData({ ...partnerFormData, notes: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Active"
                checked={partnerFormData.isActive}
                onChange={(e) => setPartnerFormData({ ...partnerFormData, isActive: e.target.checked })}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPartnerModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {selectedPartner ? 'Update' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Reject Subscription Plan Request Modal */}
      <Modal show={showRejectRequestModal} onHide={() => setShowRejectRequestModal(false)}>
        <Form onSubmit={handleRejectRequest}>
          <Modal.Header closeButton>
            <Modal.Title>Reject Subscription Request</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Notes (optional, visible to the org admin)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={rejectNotes}
                onChange={(e) => setRejectNotes(e.target.value)}
                placeholder="e.g. Please contact billing@... to arrange payment first"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowRejectRequestModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" type="submit">
              Reject Request
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Print View Modal */}
      <Modal show={showPrintModal} onHide={() => setShowPrintModal(false)} size="xl" className="print-modal">
        <Modal.Header className="print-header">
          <Modal.Title>
            {printType === 'MOVEMENT_TYPE' && 'Goods Movement Type'}
            {printType === 'CATEGORY' && 'Ledger Category'}
          </Modal.Title>
          <div className="print-actions">
            <Button variant="primary" onClick={handlePrint} className="me-2">Print</Button>
            <Button variant="secondary" onClick={() => setShowPrintModal(false)}>Close</Button>
          </div>
        </Modal.Header>
        <Modal.Body className="print-body">
          <OrgPrintChrome>
          {printType === 'MOVEMENT_TYPE' && printData && (
            <div className="print-content">
              <div className="text-center mb-4">
                <h2>GOODS MOVEMENT TYPE</h2>
                <p className="text-muted"><strong>{printData.code}</strong> — {printData.name}</p>
              </div>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>Direction:</strong> {printData.direction}</p>
                  <p><strong>Reference Type:</strong> {printData.referenceType || '-'}</p>
                  <p><strong>Location Type:</strong> {printData.locationType || 'Any (unrestricted)'}</p>
                  <p><strong>Active:</strong> {printData.isActive ? 'Yes' : 'No'}</p>
                </Col>
                <Col md={6}>
                  {printData.description && <p><strong>Description:</strong> {printData.description}</p>}
                </Col>
              </Row>
            </div>
          )}
          {printType === 'CATEGORY' && printData && (
            <div className="print-content">
              <div className="text-center mb-4">
                <h2>LEDGER CATEGORY</h2>
                <p className="text-muted"><strong>{printData.categoryName}</strong></p>
              </div>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>Type:</strong> {printData.type}</p>
                  <p><strong>Active:</strong> {printData.isActive ? 'Yes' : 'No'}</p>
                </Col>
                <Col md={6}>
                  {printData.description && <p><strong>Description:</strong> {printData.description}</p>}
                </Col>
              </Row>
            </div>
          )}
                  </OrgPrintChrome>
        </Modal.Body>
      </Modal>

      {/* Create Organization Slide-over Panel */}
      <div 
        className={`slide-over-panel ${showCreateOrgPanel ? 'show' : ''}`}
        onClick={(e) => {
          if (e.target.classList.contains('slide-over-panel')) {
            handleCloseOrgPanel();
          }
        }}
      >
        <div className="slide-over-content" onClick={(e) => e.stopPropagation()}>
          <div className="slide-over-header">
            <h4>Create Organization</h4>
            <button 
              type="button" 
              className="btn-close" 
              onClick={handleCloseOrgPanel}
              aria-label="Close"
            ></button>
          </div>
          <div className="slide-over-body">
            <Form onSubmit={handleSaveOrganization}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Organization Name *</Form.Label>
                    <Form.Control
                      type="text"
                      value={orgFormData.name}
                      onChange={(e) => setOrgFormData({ ...orgFormData, name: e.target.value })}
                      required
                      placeholder="Enter organization name"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email *</Form.Label>
                    <Form.Control
                      type="email"
                      value={orgFormData.email}
                      onChange={(e) => setOrgFormData({ ...orgFormData, email: e.target.value })}
                      required
                      placeholder="organization@example.com"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={orgFormData.description}
                  onChange={(e) => setOrgFormData({ ...orgFormData, description: e.target.value })}
                  placeholder="Organization description"
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Phone Number</Form.Label>
                    <PhoneNumberInput
                      name="phoneNumber"
                      value={orgFormData.phoneNumber}
                      onChange={(e) => setOrgFormData({ ...orgFormData, phoneNumber: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Website</Form.Label>
                    <WebAddressInput
                      https
                      value={orgFormData.website}
                      onChange={(v) => setOrgFormData({ ...orgFormData, website: v })}
                      placeholder="www.example.com"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Address</Form.Label>
                <Form.Control
                  type="text"
                  value={orgFormData.address}
                  onChange={(e) => setOrgFormData({ ...orgFormData, address: e.target.value })}
                  placeholder="Street address"
                />
              </Form.Group>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>City</Form.Label>
                    <Form.Control
                      type="text"
                      value={orgFormData.city}
                      onChange={(e) => setOrgFormData({ ...orgFormData, city: e.target.value })}
                      placeholder="City"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>District</Form.Label>
                    <Form.Control
                      type="text"
                      value={orgFormData.district}
                      onChange={(e) => setOrgFormData({ ...orgFormData, district: e.target.value })}
                      placeholder="District"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>State/Province</Form.Label>
                    {selectedOrgCountryCode && selectedOrgCountryCode !== 'OTHER' && getStatesForCountry(selectedOrgCountryCode).length > 0 ? (
                      <Form.Select
                        value={orgFormData.state}
                        onChange={(e) => setOrgFormData({ ...orgFormData, state: e.target.value })}
                      >
                        <option value="">Select State/Province</option>
                        {getStatesForCountry(selectedOrgCountryCode).map(state => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </Form.Select>
                    ) : (
                      <Form.Control
                        type="text"
                        value={orgFormData.state}
                        onChange={(e) => setOrgFormData({ ...orgFormData, state: e.target.value })}
                        placeholder="State/Province"
                      />
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Country</Form.Label>
                    <Form.Select
                      value={selectedOrgCountryCode}
                      onChange={(e) => {
                        const countryCode = e.target.value;
                        const countryObj = countries.find(c => c.code === countryCode);
                        const countryName = countryObj ? countryObj.name : '';
                        setSelectedOrgCountryCode(countryCode);
                        setOrgFormData({
                          ...orgFormData,
                          country: countryName,
                          state: '' // Reset state when country changes
                        });
                      }}
                    >
                      <option value="">Select Country</option>
                      {countries.map(country => (
                        <option key={country.code} value={country.code}>{country.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Postal Code</Form.Label>
                    <Form.Control
                      type="text"
                      value={orgFormData.postalCode}
                      onChange={(e) => setOrgFormData({ ...orgFormData, postalCode: e.target.value })}
                      placeholder="Postal Code"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Default Currency</Form.Label>
                    <Form.Select
                      value={orgFormData.defaultCurrency}
                      onChange={(e) => setOrgFormData({ ...orgFormData, defaultCurrency: e.target.value })}
                    >
                      <option value="USD">USD - US Dollar</option>
                      <option value="EUR">EUR - Euro</option>
                      <option value="GBP">GBP - British Pound</option>
                      <option value="INR">INR - Indian Rupee</option>
                      <option value="CAD">CAD - Canadian Dollar</option>
                      <option value="AUD">AUD - Australian Dollar</option>
                      <option value="JPY">JPY - Japanese Yen</option>
                      <option value="CNY">CNY - Chinese Yuan</option>
                      <option value="SGD">SGD - Singapore Dollar</option>
                      <option value="AED">AED - UAE Dirham</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Logo URL</Form.Label>
                <WebAddressInput
                  value={orgFormData.logoUrl}
                  onChange={(v) => setOrgFormData({ ...orgFormData, logoUrl: v })}
                  placeholder="example.com/logo.png"
                />
                <Form.Text className="text-muted">
                  URL to the organization's logo image
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  id="org-active-switch"
                  label="Active"
                  checked={orgFormData.isActive}
                  onChange={(e) => setOrgFormData({ ...orgFormData, isActive: e.target.checked })}
                />
              </Form.Group>

              <div className="slide-over-footer">
                <Button variant="secondary" onClick={handleCloseOrgPanel}>
                  Cancel
                </Button>
                <Button variant="primary" type="submit">
                  Create Organization
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>
      </Container>
    </>
  );
}

export default SystemAdmin;



